/**
 * WebRTCService - WebRTC connection management
 */

import { eventBus } from './EventBus';
import { signalingService, type RelayConfig } from './SignalingService';
import { iceRacingService, type RaceWinnerEvent } from './IceRacingService';
import { statsService } from './StatsService';
import { networkAdaptiveService, NetworkQuality } from './NetworkAdaptiveService';
import { clipboardQueryService } from './ClipboardQueryService';
import { pathObserver, type PathId } from './PathObserver';
import { pathMigrationService } from './PathMigrationService';
import { ConnectionState, type AppConfig } from '../types';
import { DEFAULT_CONFIG, RECONNECT_STRATEGY } from '../constants';

// adoptConnection 转交上下文：p2p 切换与 path_migrate 换路共用（docs/path_observer_design.md §4.3）
export interface AdoptConnectionContext {
  sessionId: string;
  pc: RTCPeerConnection;
  dataChannel: RTCDataChannel | null;
  inputChannel: RTCDataChannel | null;
  mouseChannel: RTCDataChannel | null;
  remoteStream: MediaStream;
  stunServers: string[];
  connectionType: 'p2p' | 'relay-direct' | 'relay-forward';
}

type RelayPathId = Exclude<PathId, 'p2p'>;

/**
 * 打印醒目的连接断开错误日志（大字红色）
 */
function logConnectionError(reason: string, details?: Record<string, unknown>): void {
  const timestamp = new Date().toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });

  // 大字红色样式
  const bigRedStyle = 'color: red; font-size: 18px; font-weight: bold;';
  const normalStyle = 'color: red; font-size: 14px;';
  const detailStyle = 'color: #ff6666; font-size: 12px;';

  console.log('%c========== WebRTC 连接异常断开 ==========', bigRedStyle);
  console.log('%c时间: ' + timestamp, normalStyle);
  console.log('%c原因: ' + reason, bigRedStyle);

  if (details) {
    console.log('%c详细信息:', normalStyle);
    Object.entries(details).forEach(([key, value]) => {
      console.log(`%c  ${key}: ${value}`, detailStyle);
    });
  }

  console.log('%c==========================================', bigRedStyle);
}

class WebRTCService {
  // 带 clientId 前缀的日志方法
  private get _tag() { return `[WebRTC][${signalingService.clientId}]`; }
  private _log(...args: any[]) { console.log(this._tag, ...args); }
  private _warn(...args: any[]) { console.warn(this._tag, ...args); }

  // 冻结当前 video 画面到 canvas，作为断线重连期间的遮罩背景（无黑屏）。
  // 生成 dataURL 通过 eventBus 发给 UI 层显示。
  private freezeLastFrameAsOverlay(): void {
    if (!this.videoElement) return;
    const w = this.videoElement.videoWidth;
    const h = this.videoElement.videoHeight;
    if (w <= 0 || h <= 0) {
      // video 尚未有画面，退化到纯色遮罩
      eventBus.emit('connection:frozenFrame', { dataUrl: null });
      return;
    }
    try {
      if (!this.lastFrameCanvas) this.lastFrameCanvas = document.createElement('canvas');
      this.lastFrameCanvas.width = w;
      this.lastFrameCanvas.height = h;
      const ctx = this.lastFrameCanvas.getContext('2d');
      if (!ctx) {
        eventBus.emit('connection:frozenFrame', { dataUrl: null });
        return;
      }
      ctx.drawImage(this.videoElement, 0, 0, w, h);
      const dataUrl = this.lastFrameCanvas.toDataURL('image/jpeg', 0.7);
      eventBus.emit('connection:frozenFrame', { dataUrl });
    } catch (e) {
      this._warn('freezeLastFrameAsOverlay failed:', e);
      eventBus.emit('connection:frozenFrame', { dataUrl: null });
    }
  }
  private _error(...args: any[]) { console.error(this._tag, ...args); }

  private pc: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private mouseChannel: RTCDataChannel | null = null;  // 鼠标专用通道
  private fsRpcChannel: RTCDataChannel | null = null;  // 文件管理器 JSON-RPC 专用通道
  private videoElement: HTMLVideoElement | null = null;
  private config: AppConfig = DEFAULT_CONFIG;
  private wsUrl: string = '';

  private _state: ConnectionState = ConnectionState.DISCONNECTED;
  private _remoteStream: MediaStream | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  private localCandidates: RTCIceCandidateInit[] = [];
  private pendingRemoteCandidates: RTCIceCandidateInit[] = [];
  private hasBeenConnected = false; // Track if we've ever successfully connected
  private trackMuted = false; // Track muted state - don't trigger reconnect when muted

  // Fast reconnect mode: 5 seconds of unlimited reconnects with no toast
  private fastReconnectMode = false;
  private fastReconnectEndTime = 0;

  // Relay mode
  private _relayConfig: RelayConfig | null = null;

  // P2P Assist: successful STUN server to use for reconnection
  private p2pAssistStunServer: string | null = null;

  // 当前主连接路径（PathObserver 只读；docs/path_observer_design.md §4.3）
  // direct=host 直连公网 TURN / forward=host 经内网 forwarder / p2p=直连
  private _currentPath: PathId = 'direct';
  // 整连重连时的 relay 路径目标。forward 一旦接管成功，后续 race_init 必须继续 force forward。
  private _desiredRelayPath: RelayPathId = 'direct';
  private decodeStallRecoveryTimer: number | null = null;
  // forced forward 逃生通道：desiredRelayPath=forward 期间 race 连续失败计数，
  // ≥2 次回退 direct。否则 forwarder 死亡时重连永远 force forward → 死循环到刷新页面
  //（与 hostInternalTurnOnly 0-TURN 死区同构：单一来源排除必须有 fallback）
  private forwardConnectFailures = 0;

  // Clipboard chunking buffer
  private clipboardChunks: Map<number, string> = new Map();
  private clipboardTotalChunks = 0;

  // 主动断开标志（用于区分用户主动断开和异常断开）
  private intentionalDisconnect = false;

  // 最后一帧冻结 canvas（P2P 断线时作为"重连中"遮罩背景，避免黑屏）
  private lastFrameCanvas: HTMLCanvasElement | null = null;

  // [TCP 修复 C] race winner 后等 DC 真就绪才弹 toast / setState(CONNECTED)
  private pendingWinnerToast: { text: string; ms: number } | null = null;
  // [TCP 修复 C] DC 在 winner 后 8 秒内不就绪就自动重连
  private dcReadyTimeoutTimer: number | null = null;

  // Visibility pause state (tab hidden for >3s)
  private visibilityPauseTimer: number | null = null;
  private visibilityPaused = false;
  private boundVisibilityHandler: (() => void) | null = null;

  // Idle detection state
  private idleTimer: number | null = null;
  private idleCountdownTimer: number | null = null;
  private idleCountdown = 0;
  private idlePaused = false;
  private _idleBlocking = false;               // 阻断输入发送
  private idleBlockingTimer: number | null = null;
  private readonly IDLE_TIMEOUT = new URLSearchParams(window.location.search).get('isdebug') === '1' ? 5000 : 3600000;  // 60分钟
  private readonly IDLE_COUNTDOWN = 10;        // 倒计时秒数
  private boundIdleHandler: (() => void) | null = null;

  get state(): ConnectionState {
    return this._state;
  }

  get remoteStream(): MediaStream | null {
    return this._remoteStream;
  }

  get isConnected(): boolean {
    return this._state === ConnectionState.CONNECTED;
  }

  /** 空闲检测期间阻断输入 */
  get idleBlocking(): boolean {
    return this._idleBlocking;
  }

  get inputChannel(): RTCDataChannel | null {
    return this.dataChannel;
  }

  get mouseInputChannel(): RTCDataChannel | null {
    return this.mouseChannel;
  }

  get fsRpcInputChannel(): RTCDataChannel | null {
    return this.fsRpcChannel;
  }

  get peerConnection(): RTCPeerConnection | null {
    return this.pc;
  }

  get isRelayMode(): boolean {
    return this._relayConfig !== null;
  }

  get relayConfig(): RelayConfig | null {
    return this._relayConfig;
  }

  // Bound event handlers (to preserve 'this' context and allow removal)
  private boundHandleOffer = this.handleOffer.bind(this);
  private boundHandleCandidate = this.handleRemoteCandidate.bind(this);
  private boundHandleSignalingClose = this.handleSignalingClose.bind(this);
  private boundSendJitterBuffer = this.sendJitterBuffer.bind(this);
  private boundSendNetworkStats = this.sendNetworkStats.bind(this);
  private boundHandleAgentRestart = this.handleAgentRestart.bind(this);
  private boundHandleRaceOffer = this.handleRaceOffer.bind(this);
  private boundHandleRaceCandidate = this.handleRaceCandidate.bind(this);
  private boundHandleRaceWinner = this.handleRaceWinner.bind(this);
  private boundHandleRaceFailed = this.handleRaceFailed.bind(this);
  private listenersRegistered = false;

  // ICE Racing mode
  private useIceRacing = true;  // Enable ICE Racing by default

  /**
   * Initialize with configuration
   */
  initialize(config: Partial<AppConfig>, videoElement: HTMLVideoElement, wsUrl?: string): void {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.videoElement = videoElement;
    if (wsUrl) {
      this.wsUrl = wsUrl;
    }
    this.hasBeenConnected = false;

    // Register event listeners only once
    if (!this.listenersRegistered) {
      eventBus.on('webrtc:offer', this.boundHandleOffer);
      eventBus.on('webrtc:candidate', this.boundHandleCandidate);
      eventBus.on('signaling:close', this.boundHandleSignalingClose);
      eventBus.on('stats:sendJitterBuffer', this.boundSendJitterBuffer);
      eventBus.on('stats:sendNetworkStats', this.boundSendNetworkStats);
      eventBus.on('webrtc:requestKeyframe', () => this.requestKeyframe());
      eventBus.on('agent:restart', this.boundHandleAgentRestart);
      eventBus.on('p2p_assist:performSwitch', this.handleP2PAssistPerformSwitch.bind(this));

      // Sync 打洞控制消息：P2PAssistService 发起，经 relay PC 的 data DC 送出
      eventBus.on('p2p_assist:ctrl_send', (payload: Record<string, unknown>) => {
        if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
          console.warn('[WebRTC] p2p_assist:ctrl_send skipped: data channel not open');
          return;
        }
        try {
          this.dataChannel.send(JSON.stringify(payload));
        } catch (e) {
          console.warn('[WebRTC] p2p_assist:ctrl_send failed:', e);
        }
      });

      // ICE Racing event handlers
      eventBus.on('race:offer', this.boundHandleRaceOffer);
      eventBus.on('race:candidate', this.boundHandleRaceCandidate);
      eventBus.on('race:winner', this.boundHandleRaceWinner);
      eventBus.on('race:failed', this.boundHandleRaceFailed);

      // Peer status for ICE Racing (Client-driven)
      eventBus.on('peer:status', this.handlePeerStatus.bind(this));

      // Host offline detection - clean up old PC immediately for fast reconnect
      eventBus.on('host:waiting', this.handleHostWaiting.bind(this));

      // 持续丢包自动切换到 TURN
      eventBus.on('stats:persistentPacketLoss', this.handlePersistentPacketLoss.bind(this));

      // 统一「视频卡死」事件（fps=0 持续过久）：单一决策按 reason 分流（B0 中枢）
      eventBus.on('video:stuck', this.boundHandleVideoStuck);

      this.listenersRegistered = true;
    }
  }

  /**
   * Handle P2P assist seamless switch - switch video AND control channels to P2P connection
   * After successful switch, close the original TURN connection
   *
   * 核心转交逻辑已抽为 adoptConnection（p2p 与 path_migrate 共用），此处仅做参数适配，
   * 原 p2p_assist:performSwitch 事件路径行为不变（已上线功能，回归红线）。
   */
  private handleP2PAssistPerformSwitch(data: {
    sessionId: string;
    pc: RTCPeerConnection;
    dataChannel: RTCDataChannel | null;
    inputChannel: RTCDataChannel | null;
    mouseChannel: RTCDataChannel | null;
    fileCtrlChannel: RTCDataChannel | null;
    fileDataChannel: RTCDataChannel | null;
    remoteStream: MediaStream;
    stunServers: string[];
    connectionType: string;
  }): void {
    this.adoptConnection({
      sessionId: data.sessionId,
      pc: data.pc,
      dataChannel: data.dataChannel,
      inputChannel: data.inputChannel,
      mouseChannel: data.mouseChannel,
      remoteStream: data.remoteStream,
      stunServers: data.stunServers,
      connectionType: 'p2p', // P2PAssist 切换语义恒为 p2p（其 ctx.connectionType 默认即 'p2p'）
    });
  }

  /** 当前主连接路径（PathObserver 读取） */
  get currentPath(): PathId {
    return this._currentPath;
  }

  get desiredRelayPath(): RelayPathId {
    return this._desiredRelayPath;
  }

  private setDesiredRelayPath(p: RelayPathId, reason: string): void {
    const old = this._desiredRelayPath;
    this._desiredRelayPath = p;
    if (old !== p) {
      this._log(`desiredRelayPath=${p} (from=${old}, reason=${reason})`);
    } else {
      this._log(`desiredRelayPath=${p} (reason=${reason})`);
    }
  }

  private getInitialRelayPath(preservePath: boolean): RelayPathId {
    if (this.config.hostInternalTurnOnly) return 'forward';
    return preservePath ? this._desiredRelayPath : 'direct';
  }

  private setCurrentPath(p: PathId, reason: 'initial' | 'internal_only' | 'migrated' | 'p2p_switch' | 'preserved_reconnect'): void {
    this._currentPath = p;
    if (p !== 'p2p') {
      this.setDesiredRelayPath(p, `currentPath:${reason}`);
    }
    this._log(`currentPath=${p} (reason=${reason})`);
  }

  /**
   * 公共连接转交：把已就绪的备用 PC（p2p 或 path_migrate 的 relay 备连）切为主连接——
   * 换主 PC/控制通道/视频流挂载/关旧 PC/请求关键帧/状态广播，并维护 currentPath。
   * docs/path_observer_design.md §4.3
   */
  adoptConnection(data: AdoptConnectionContext): void {
    const isP2P = data.connectionType === 'p2p';
    const label = isP2P ? 'P2P' : 'PathMigrate';
    if (isP2P && pathMigrationService.active) {
      pathMigrationService.abort('p2p_manual_switch');
    }
    this._log(`adoptConnection: type=${data.connectionType} from=${this._currentPath} session=${data.sessionId}`);
    const diagTag = isP2P ? '[P2P-SWITCH-DIAG]' : '[PM-SWITCH-DIAG]';
    const diagLines: string[] = [];
    const diag = (msg: string) => {
      const ts = new Date().toISOString();
      const line = `${ts} ${diagTag} ${msg}`;
      diagLines.push(line);
      console.log(line);
    };

    diag(`====== ${label} 切换诊断开始 ======`);
    diag(`sessionId=${data.sessionId}, stunServers=${data.stunServers.join(', ')}`);

    // 保存原有PC引用（稍后关闭）
    const oldPc = this.pc;
    const oldDataChannel = this.dataChannel;
    const oldMouseChannel = this.mouseChannel;

    // 置空懒加载通道（旧通道随 oldPc 关闭自动失效，置空以触发下次懒创建）
    this.fsRpcChannel = null;

    // 切换到P2P的PC和通道
    const p2pPc = data.pc;

    // 诊断：切换前全量通道状态
    diag(`inputChannel: label=${data.inputChannel?.label ?? 'null'}, readyState=${data.inputChannel?.readyState ?? 'null'}`);
    diag(`dataChannel(p2p-assist): label=${data.dataChannel?.label ?? 'null'}, readyState=${data.dataChannel?.readyState ?? 'null'}`);
    diag(`mouseChannel: label=${data.mouseChannel?.label ?? 'null'}, readyState=${data.mouseChannel?.readyState ?? 'null'}`);
    diag(`oldDataChannel: label=${oldDataChannel?.label ?? 'null'}, readyState=${oldDataChannel?.readyState ?? 'null'}`);
    diag(`${label}-PC: iceState=${p2pPc.iceConnectionState}, connState=${p2pPc.connectionState}, signalingState=${p2pPc.signalingState}`);

    // 诊断：remoteStream track 状态
    const videoTracks = data.remoteStream.getVideoTracks();
    const audioTracks = data.remoteStream.getAudioTracks();
    diag(`remoteStream: ${videoTracks.length} video, ${audioTracks.length} audio`);
    videoTracks.forEach((t, i) => {
      diag(`videoTrack[${i}]: id=${t.id}, readyState=${t.readyState}, muted=${t.muted}, enabled=${t.enabled}`);
    });

    // P2P 切换前记录关键帧基线：供下方 500ms / 3s 两个诊断 setTimeout 共用。
    // 必须声明在 if 块外层，否则 3s setTimeout 跨作用域引用会触发 ReferenceError。
    const baselineKfCount = statsService.currentStats.keyframesDecoded;

    // 切换视频流到P2P连接
    if (this.videoElement && data.remoteStream) {
      if (videoTracks.length === 0) {
        diag('WARNING: remoteStream 无 video track，将黑屏');
      }
      this.videoElement.srcObject = data.remoteStream;
      this._remoteStream = data.remoteStream;
      diag('videoElement.srcObject 已切换');

      // 尝试播放
      this.videoElement.play().catch((e) => {
        diag(`videoElement.play() 失败: ${e.message}`);
      });

      // P2P 切换后请求关键帧：成功即停模式
      const requestKeyframeViaChannel = () => {
        if (data.inputChannel?.readyState === 'open') {
          data.inputChannel.send(JSON.stringify({ type: 'request_keyframe' }));
          diag(`requestKeyframe 发送成功: channel=${data.inputChannel.label}, readyState=open`);
          return true;
        }
        diag(`requestKeyframe 通过 inputChannel 失败: readyState=${data.inputChannel?.readyState ?? 'null'}, 尝试 this.dataChannel fallback`);
        return false;
      };

      const sendKeyframeRequest = (label: string) => {
        diag(`requestKeyframe(${label}): inputChannel.readyState=${data.inputChannel?.readyState ?? 'null'}, this.dataChannel.readyState=${this.dataChannel?.readyState ?? 'null'}`);
        if (!requestKeyframeViaChannel()) {
          if (this.dataChannel?.readyState === 'open') {
            this.requestKeyframe();
            diag(`requestKeyframe(${label}): 通过 this.dataChannel(label=${this.dataChannel.label}) 发送`);
          } else {
            diag(`requestKeyframe(${label}): this.dataChannel 也不可用, readyState=${this.dataChannel?.readyState ?? 'null'}, 关键帧请求丢失!`);
          }
        }
      };

      sendKeyframeRequest('initial');

      // 500ms 后检查是否收到新关键帧，没收到才补发（baselineKfCount 已在 if 块外取得）
      diag(`keyframesDecoded(baseline)=${baselineKfCount}`);
      setTimeout(() => {
        const currentKf = statsService.currentStats.keyframesDecoded;
        if (currentKf > baselineKfCount) {
          diag(`keyframesDecoded 500ms 后: ${baselineKfCount} -> ${currentKf}, 关键帧已到达`);
          return;
        }
        diag(`keyframesDecoded 500ms 后: ${baselineKfCount} -> ${currentKf}, 无增长, 补发关键帧`);
        sendKeyframeRequest('retry-1');
      }, 500);
    } else {
      diag(`WARNING: videoElement=${!!this.videoElement}, remoteStream=${!!data.remoteStream}, 跳过视频切换`);
    }

    // 切换控制通道到P2P
    if (data.inputChannel?.readyState === 'open') {
      diag(`dataChannel 切换: 旧=${oldDataChannel?.label ?? 'null'}(${oldDataChannel?.readyState ?? 'null'}) -> 新=inputChannel(${data.inputChannel.label})`);
      this.dataChannel = data.inputChannel;
      this.setupDataChannelEvents();
    } else {
      diag(`WARNING: inputChannel.readyState=${data.inputChannel?.readyState ?? 'null'}, 保留旧 dataChannel(${oldDataChannel?.label ?? 'null'}), 旧通道即将被关闭!`);
    }

    if (data.mouseChannel?.readyState === 'open') {
      diag(`mouseChannel 切换: -> ${data.mouseChannel.label}`);
      this.mouseChannel = data.mouseChannel;
      eventBus.emit('webrtc:mouseChannel', this.mouseChannel);
    } else {
      diag(`WARNING: mouseChannel.readyState=${data.mouseChannel?.readyState ?? 'null'}, 保留旧 mouseChannel`);
    }

    // file-ctrl / file-data 改为懒加载，由 Host ondatachannel 提供或按需创建
    // P2P 切换后用户打开文件管理器或触发 Ctrl+C/V 时自动在新 PC 上创建

    // 设置新主 PC 的 ondatachannel 处理（接收 Host 创建的 file-ctrl/file-data 等）
    this.setupOnDataChannelHandler(p2pPc);

    // 设置新主 PC 的事件处理（用于监控连接状态）。
    // disconnected 给 5s 自愈窗口（race winner 同款范式）——瞬时抖动常可恢复，
    // 立即整连会小题大做；failed 才立即重连。复用 raceWinnerDisconnectTimer：
    // 同一时刻只有一个主 PC，cleanup 已统一清理该定时器。
    p2pPc.oniceconnectionstatechange = () => {
      const state = p2pPc.iceConnectionState;
      this._log(`${label} PC ICE state:`, state);

      // 状态变化先清挂起的断线定时器
      if (this.raceWinnerDisconnectTimer) {
        clearTimeout(this.raceWinnerDisconnectTimer);
        this.raceWinnerDisconnectTimer = null;
      }

      if (state === 'disconnected') {
        this._log(`${label} ICE disconnected, waiting 5s for recovery...`);
        this.raceWinnerDisconnectTimer = window.setTimeout(() => {
          this.raceWinnerDisconnectTimer = null;
          if (p2pPc.iceConnectionState !== 'disconnected') return;
          if (!this.intentionalDisconnect) {
            logConnectionError(`${label}连接断开超过5秒未恢复`, {
              connectionState: p2pPc.connectionState,
              stunServers: data.stunServers.join(', '),
            });
            // 无黑屏回退：冻结最后一帧作为遮罩背景 + 显示"连接中"提示，然后触发重连
            this.freezeLastFrameAsOverlay();
            eventBus.emit('connection:reconnecting', { reason: `${label}连接断开` });
            // 业务层断线重连：scheduleReconnect 仅在 DISCONNECTED 状态退出，CONNECTED 可进入
            this.scheduleReconnect(false);
          } else {
            eventBus.emit('ui:toast', { message: `${label}连接断开`, type: 'warning' });
          }
        }, 5000);
      } else if (state === 'failed') {
        if (!this.intentionalDisconnect) {
          logConnectionError(`${label}连接失败`, {
            iceState: state,
            connectionState: p2pPc.connectionState,
            stunServers: data.stunServers.join(', '),
          });
          this.freezeLastFrameAsOverlay();
          eventBus.emit('connection:reconnecting', { reason: `${label}连接失败` });
          this.scheduleReconnect(false);
        } else {
          eventBus.emit('ui:toast', { message: `${label}连接断开`, type: 'warning' });
        }
      } else if (state === 'connected' || state === 'completed') {
        // 5s 窗口内自愈：取消已排队的重连，状态拉回（race winner 恢复分支同款）
        this._log(`${label} ICE recovered:`, state);
        this.stopReconnect();
        if (this._state === ConnectionState.RECONNECTING) {
          this.setState(ConnectionState.CONNECTED);
        }
      }
    };

    p2pPc.onconnectionstatechange = () => {
      this._log(`${label} PC connection state:`, p2pPc.connectionState);
    };

    // 监控新主连接 track 状态
    data.remoteStream.getTracks().forEach(track => {
      track.onended = () => {
        this._log(`${label} track ENDED:`, track.kind, track.readyState);
      };
      track.onmute = () => {
        this._log(`${label} track MUTED:`, track.kind);
      };
      track.onunmute = () => {
        this._log(`${label} track UNMUTED:`, track.kind);
      };
    });

    // B5-step4：换 PC 前清理关键帧周期重发定时器（旧 PC baseline 不再可信）
    if (this.keyframeRetryTimer !== null) {
      clearInterval(this.keyframeRetryTimer);
      this.keyframeRetryTimer = null;
    }

    // 更新主 PC 引用为新主 PC
    this.pc = p2pPc;

    // 关闭原有的TURN连接
    if (oldPc) {
      this._log('Closing original TURN connection...');

      // 先关闭原有DataChannel
      if (oldDataChannel) {
        oldDataChannel.onopen = null;
        oldDataChannel.onclose = null;
        oldDataChannel.onerror = null;
        oldDataChannel.onmessage = null;
        oldDataChannel.close();
      }
      if (oldMouseChannel) {
        oldMouseChannel.onopen = null;
        oldMouseChannel.onclose = null;
        oldMouseChannel.onerror = null;
        oldMouseChannel.close();
      }

      // 关闭原有PC
      oldPc.onicecandidate = null;
      oldPc.oniceconnectionstatechange = null;
      oldPc.ontrack = null;
      oldPc.ondatachannel = null;
      oldPc.close();
      this._log('Original TURN connection closed');

      // 验证新主 PC 状态未受影响
      this._log(`After closing old PC - ${label} PC state:`, p2pPc.connectionState, 'ICE:', p2pPc.iceConnectionState);
      data.remoteStream.getTracks().forEach(track => {
        this._log(`After closing - ${label} track:`, track.kind, 'readyState:', track.readyState, 'muted:', track.muted);
      });
    }

    // 通知 FileTransferService 更新 PC 引用（旧 PC 已关闭）
    this._log('Emitting pcUpdated for FileTransferService');
    eventBus.emit('connection:pcUpdated', { pc: p2pPc });

    // 更新连接状态
    const serversStr = data.stunServers.join(', ');
    this._log(`Full switch to ${label} completed`);
    this._log(`Video: ${label} via`, serversStr);
    this._log(`Input: ${label} via`, serversStr);
    this._log('Original TURN connection: CLOSED');

    // 通知其他服务更新PC引用（如StatsService）
    eventBus.emit('webrtc:pcSwitched', {
      pc: p2pPc,
      stunServers: data.stunServers,
    });

    eventBus.emit('connection:switched', {
      type: isP2P ? 'p2p' : 'relay',
      stunServers: data.stunServers,
      videoOnly: false,  // 完全切换到新主连接（包括控制通道）
      pc: p2pPc,
    });

    if (isP2P) {
      // P2P Assist 切换完成，重置 ICE/TURN 配置为自动；pm relay 迁移不改变用户配置
      this.config.icePolicy = 'auto';
      this.config.turnProtocol = 'auto';
      eventBus.emit('config:icePolicyChange', 'auto');
      eventBus.emit('config:autoReset');
    }

    // currentPath 维护 + PathObserver 记账：
    // p2p 切换的完成信号在此回执；pm 迁移的 onSwitchDone 由 PathMigrationService 的 onDone 回调驱动（不重复回执）
    const newPath: PathId = isP2P ? 'p2p' : (data.connectionType === 'relay-forward' ? 'forward' : 'direct');
    this.setCurrentPath(newPath, isP2P ? 'p2p_switch' : 'migrated');
    if (isP2P) {
      pathObserver.onSwitchDone('p2p', true);
    }

    // 3 秒后全量诊断检查
    setTimeout(() => {
      const vt = data.remoteStream.getVideoTracks();
      const anyMuted = vt.some(t => t.muted);
      const allEnded = vt.every(t => t.readyState === 'ended');
      const kfAfter = statsService.currentStats.keyframesDecoded;

      diag('====== 切换后 3s 检查 ======');
      diag(`keyframesDecoded: baseline=${baselineKfCount} -> now=${kfAfter}, delta=${kfAfter - baselineKfCount}`);
      diag(`PC: iceState=${p2pPc.iceConnectionState}, connState=${p2pPc.connectionState}`);
      diag(`this.dataChannel: label=${this.dataChannel?.label ?? 'null'}, readyState=${this.dataChannel?.readyState ?? 'null'}`);
      diag(`this.mouseChannel: label=${this.mouseChannel?.label ?? 'null'}, readyState=${this.mouseChannel?.readyState ?? 'null'}`);
      vt.forEach((t, i) => {
        diag(`videoTrack[${i}]: readyState=${t.readyState}, muted=${t.muted}, enabled=${t.enabled}`);
      });

      if (allEnded) {
        diag('RESULT: FAILED - 所有 video track 已 ended');
        eventBus.emit('ui:toast', { message: `${label}视频流已断开`, type: 'error' });
      } else if (kfAfter <= baselineKfCount) {
        diag('RESULT: FAILED - 3s 内 keyframesDecoded 无增长，解码器未收到关键帧');
        if (data.inputChannel?.readyState === 'open') {
          data.inputChannel.send(JSON.stringify({ type: 'request_keyframe' }));
          diag('补发 requestKeyframe via inputChannel');
        }
      } else if (anyMuted) {
        diag('RESULT: WARNING - video track muted，但 keyframesDecoded 有增长');
      } else {
        diag('RESULT: OK - 视频正常播放');
      }

      diag(`====== ${label} 切换诊断结束 ======`);

      // 失败时下载诊断文件（默认关闭，避免打扰用户）
      // 排查时在浏览器控制台执行 localStorage.setItem('p2p_diag_download','1') 开启，
      // 关闭则 localStorage.removeItem('p2p_diag_download')
      let diagDownloadEnabled = false;
      try {
        diagDownloadEnabled = localStorage.getItem('p2p_diag_download') === '1';
      } catch {}
      if (diagDownloadEnabled && (allEnded || kfAfter <= baselineKfCount)) {
        try {
          const blob = new Blob([diagLines.join('\n')], { type: 'text/plain' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `p2p_switch_diag_${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
          a.click();
          URL.revokeObjectURL(url);
        } catch {}
      }
    }, 3000);
  }

  /**
   * 处理持续丢包事件 - 自动切换到 TURN（仅当前会话生效，刷新后重新尝试 P2P）
   */
  private handlePersistentPacketLoss(data: { packetLoss: number; duration: number }): void {
    const currentStats = statsService.currentStats;
    const currentMode = currentStats.transportType === 'relay'
      ? `中转-${currentStats.relayProtocol || 'UDP'}`
      : 'P2P';
    console.log(`[WebRTC] 检测到持续丢包: ${data.packetLoss.toFixed(1)}%, 持续 ${(data.duration / 1000).toFixed(1)}秒, 当前: ${currentMode}`);

    // 如果已经是中转 TCP，不需要切换，但降低 FPS 来应对丢包
    if (currentStats.transportType === 'relay' && currentStats.relayProtocol === 'TCP') {
      this._log('已经是 TURN TCP 模式，通知 NetworkAdaptive 降级');
      networkAdaptiveService.forceConfig(NetworkQuality.BAD);
      eventBus.emit('ui:toast', {
        message: '网络不稳定，已降低帧率',
        type: 'warning',
      });
      return;
    }

    // 重置为自动模式，由 ICE Racing relay-first 策略自动选择最优线路
    this.config.icePolicy = 'auto';
    this.config.turnProtocol = 'auto';

    // 通知 UI 同步重置
    eventBus.emit('config:icePolicyChange', 'auto');
    eventBus.emit('config:autoReset');

    // 显示提示
    eventBus.emit('ui:toast', {
      message: '检测到网络丢包，正在自动重连...',
      type: 'warning',
    });

    // 触发重连，ICE Racing relay-first 策略会自动选择最优线路
    this._log('触发重连（auto 模式，relay-first）');
    this.intentionalDisconnect = false;
    this.cleanup();
    signalingService.disconnect();
    this._state = ConnectionState.DISCONNECTED;
    this.reconnectAttempt = 0; // 重置重连计数

    // 延迟重连，让用户看到提示
    setTimeout(() => {
      if (this._relayConfig) {
        this.connectRelay(this._relayConfig);
      } else if (this.wsUrl) {
        this.connect(this.wsUrl);
      }
    }, 500);
  }

  /**
   * 统一「视频卡死」决策（B0 中枢）：StatsService 单一检测器 emit 'video:stuck'，此处按 reason 分流
   *   'codec-undecodable'  → 已是 h264 则警告返回（防无限环）；否则换 H264 重连（#4）
   *   'first-frame-stall'  → B4 收权（§4.5）：先交 PathObserver 后台无缝换路，被拦/失败才降级原 B4（排除 winner TURN+整连重连）
   *   'stall-after-decode' → 同路径整连重连（不换路径/不换 codec，依赖 host Unsubscribe 清零 BWE 恢复），不经 Observer
   */
  private boundHandleVideoStuck = this.handleVideoStuck.bind(this);

  private handleVideoStuck(p: { reason: string; neverDecoded: boolean; duration: number; framesReceived: number; currentCodec: string }): void {
    console.log(`[WebRTC] 视频卡死(reason=${p.reason}, ${(p.duration / 1000).toFixed(0)}s, recv=${p.framesReceived})`);

    if (p.reason === 'codec-undecodable') {
      if ((this.config.videoCodec || 'auto') === 'h264') {
        this._warn('[CodecGuard] already h264 but still black, skip codec fallback'); return; // 防无限环
      }
      eventBus.emit('ui:toast', { message: '视频解码失败，正在切换到兼容编码(H264)重连', type: 'warning' });
      this.reconnectWithNewConfig({ videoCodec: 'h264' });  // 复用已验证强制 codec 通路 → race_init.codecs[0]='H264'
      return;
    }

    if (p.reason === 'first-frame-stall') {
      // B4 决策收权（docs/path_observer_design.md §4.5）：卡死信号先交 PathObserver 后台换路
      // （make-before-break，不断流）；被防护拦下或换路失败时回调降级回原 B4 逻辑。
      // toast 移入降级路径：换路成功时无断流，不该提示"正在重连"。
      pathObserver.onStallSignal(() => this.b4StallReconnect({
        excludeBadTurn: true,
        preservePath: true,
        reason: 'first-frame-stall-path-switch-blocked',
      }));
      return;
    }
    // stall-after-decode：先请求关键帧并短窗口复核；仍无恢复时交 PathObserver 做 make-before-break。
    this.recoverDecodeStall();
  }

  private recoverDecodeStall(): void {
    if (this.decodeStallRecoveryTimer !== null) {
      this._log('decode-stall recovery already pending, ignore duplicate signal');
      return;
    }
    const before = {
      framesDecoded: statsService.currentStats.framesDecoded,
      framesReceived: statsService.currentStats.framesReceived,
      keyframesDecoded: statsService.currentStats.keyframesDecoded,
    };
    this._log('decode-stall recovery: request keyframe before path action', before);
    this.requestKeyframe();
    this.decodeStallRecoveryTimer = window.setTimeout(() => {
      this.decodeStallRecoveryTimer = null;
      if (this._state !== ConnectionState.CONNECTED) {
        this._log('decode-stall recovery skipped: state=', this._state);
        return;
      }
      const after = statsService.currentStats;
      const progressed =
        after.framesDecoded > before.framesDecoded ||
        after.keyframesDecoded > before.keyframesDecoded;
      if (progressed) {
        this._log('decode-stall recovered after keyframe request', {
          framesDecoded: `${before.framesDecoded}->${after.framesDecoded}`,
          framesReceived: `${before.framesReceived}->${after.framesReceived}`,
          keyframesDecoded: `${before.keyframesDecoded}->${after.keyframesDecoded}`,
        });
        return;
      }
      this._log('decode-stall not recovered, delegate to PathObserver');
      pathObserver.onStallSignal(() => this.b4StallReconnect({
        excludeBadTurn: false,
        preservePath: true,
        reason: 'stall-after-decode-path-switch-blocked',
      }));
    }, 2000);
  }

  // 原 B4 断流重连骨架（保留，作为 PathObserver 换路失败/被拦时的降级路径）
  private b4StallReconnect(options: { excludeBadTurn: boolean; preservePath: boolean; reason: string }): void {
    this.fullReconnect({
      ...options,
      toastMessage: '视频流无响应，正在自动重连...',
    });
  }

  private fullReconnect(options: {
    reason: string;
    preservePath: boolean;
    excludeBadTurn?: boolean;
    softDisconnect?: boolean;
    toastMessage?: string;
  }): void {
    if (options.toastMessage) {
      eventBus.emit('ui:toast', { message: options.toastMessage, type: 'warning' });
    }
    const initialPath = this.getInitialRelayPath(options.preservePath);
    this.setDesiredRelayPath(initialPath, `fullReconnect:${options.reason}`);
    this._log(`full reconnect: reason=${options.reason} preservePath=${options.preservePath} initialPath=${initialPath} currentPath=${this._currentPath}`);

    if (options.excludeBadTurn) {
      const badTurn = iceRacingService.lastWinnerTurnHost;     // 仅首连 never-decode 才换 TURN 路径
      if (badTurn) iceRacingService.setExcludeTurnHost(badTurn);
    }
    this.stopReconnect();
    this.intentionalDisconnect = false;
    this.cleanup();
    signalingService.disconnect();
    this._state = ConnectionState.DISCONNECTED;
    this.reconnectAttempt = 0;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (this._relayConfig) this.connectRelay(this._relayConfig);
      else if (this.wsUrl) this.connect(this.wsUrl);
    }, options.softDisconnect ? 250 : 500);
  }

  /**
   * Connect to remote peer (direct mode)
   */
  async connect(wsUrl?: string): Promise<void> {
    if (this._state !== ConnectionState.DISCONNECTED && this._state !== ConnectionState.FAILED) {
      return;
    }

    if (wsUrl) {
      this.wsUrl = wsUrl;
    }
    this._relayConfig = null; // Clear relay config for direct mode

    this.setState(ConnectionState.CONNECTING);
    this.localCandidates = [];
    this.pendingRemoteCandidates = [];

    try {
      await signalingService.connect(this.wsUrl);
      this.setState(ConnectionState.SIGNALING);
    } catch (e) {
      this._error('Connection failed:', e);
      this.handleInitialConnectionError(e as Error);
    }
  }

  /**
   * Connect via relay server
   */
  async connectRelay(config: RelayConfig): Promise<void> {
    if (this._state !== ConnectionState.DISCONNECTED && this._state !== ConnectionState.FAILED) {
      return;
    }

    this._relayConfig = config;
    this.wsUrl = ''; // Clear direct URL

    this.setState(ConnectionState.CONNECTING);
    this.localCandidates = [];
    this.pendingRemoteCandidates = [];

    try {
      await signalingService.connectRelay(config);
      this.setState(ConnectionState.SIGNALING);
      this._log('Connected to relay, waiting for Host...');

      // Start ICE Racing when Host comes online (handled by peer:status event)
      // The racing will be started when we receive race:offer events
      if (this.useIceRacing) {
        this._log('ICE Racing mode enabled');
      }
    } catch (e) {
      this._error('Relay connection failed (will be retried by SignalingService):', e);
      // 信令仍在重连退避周期中：保持 CONNECTING，让 ConnectionOverlay 继续显示"连接中"。
      // 真正成功靠 server 推 peer_status:online → handlePeerStatus 推进到 SIGNALING。
      if (signalingService.waitingForHost) {
        this._log('Signaling reconnecting, keep CONNECTING state (no FAILED transition)');
        return;
      }
      this.handleInitialConnectionError(e as Error);
    }
  }

  /**
   * Handle initial connection error (server will ICE restart)
   */
  private handleInitialConnectionError(error: Error): void {
    this._error('Initial connection error:', error);
    // Don't emit error event - server will send ICE Restart
    // Just set state to FAILED so we can detect restart
    this.setState(ConnectionState.FAILED);
  }

  /**
   * Disconnect from remote peer
   */
  disconnect(): void {
    this.intentionalDisconnect = true; // 标记为主动断开
    this.stopReconnect();
    // B5-step4：清理关键帧周期重发定时器
    if (this.keyframeRetryTimer !== null) {
      clearInterval(this.keyframeRetryTimer);
      this.keyframeRetryTimer = null;
    }
    this.cleanup();
    signalingService.disconnect();
    this.hasBeenConnected = false;
    this.reconnectAttempt = 0;
    this._relayConfig = null;
    this.setState(ConnectionState.DISCONNECTED);
  }

  /**
   * Reconnect with new configuration (e.g., ICE policy change)
   * This will disconnect and reconnect with the updated config
   */
  reconnectWithNewConfig(partialConfig: Partial<typeof this.config>): void {
    // Update config
    const oldPolicy = this.config.icePolicy;
    this.config = { ...this.config, ...partialConfig };
    this._log('Reconnecting with new config:', partialConfig);
    this._log('ICE Policy changed:', oldPolicy, '->', this.config.icePolicy);

    // Notify P2PAssistService about icePolicy change
    if (partialConfig.icePolicy) {
      eventBus.emit('config:icePolicyChange', this.config.icePolicy);
    }

    // If currently connected, trigger a reconnect
    // Support both direct WebSocket mode (wsUrl) and relay mode (_relayConfig)
    const isConnected = this._state === ConnectionState.CONNECTED;
    const hasDirectUrl = !!this.wsUrl;
    const hasRelayConfig = !!this._relayConfig;

    if (isConnected && (hasDirectUrl || hasRelayConfig)) {
      const savedWsUrl = this.wsUrl;
      const savedRelayConfig = this._relayConfig;
      // Stop any existing reconnect timers
      this.stopReconnect();
      // Clean up current connection
      this.cleanup();
      signalingService.disconnect();
      // Set state to DISCONNECTED so connect() will work
      this._state = ConnectionState.DISCONNECTED;
      // Small delay then reconnect
      setTimeout(() => {
        this.reconnectAttempt = 0;
        if (savedRelayConfig) {
          this._log('Executing relay reconnect with icePolicy:', this.config.icePolicy);
          this.connectRelay(savedRelayConfig);
        } else if (savedWsUrl) {
          this._log('Executing reconnect to:', savedWsUrl, 'with icePolicy:', this.config.icePolicy);
          this.connect(savedWsUrl);
        }
      }, 500);
    } else {
      this._log('Not connected or no connection info, skipping reconnect. State:', this._state, 'wsUrl:', !!this.wsUrl, 'relayConfig:', !!this._relayConfig);
    }
  }

  /**
   * Send data through data channel
   */
  sendData(data: string | ArrayBuffer): boolean {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(data as string);
      return true;
    }
    return false;
  }

  /**
   * Request keyframe from server
   * Note: DLL will allow first IDR for decoder init, then ignore subsequent requests
   * (Intra Refresh only mode for low latency)
   */
  requestKeyframe(): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({ type: 'request_keyframe' }));
    }
  }

  // ===== B5 前端关键帧周期重发（成功即停）=====
  // 单一 keyframeRetryTimer：ontrack 与 dc-open 两路天然去重，不叠加
  private keyframeRetryTimer: number | null = null;
  private keyframeRetryCount = 0;

  /**
   * 周期重发 request_keyframe，直到解码出新 IDR（keyframesDecoded 增长）或达到上限（15 次 × 200ms ≈ 3s）。
   * 停条件用 keyframesDecoded（语义=IDR 到达），不是 framesDecoded（reviewer 修正 (a)）。
   * baseline 残留（reviewer 修正 (b)）：TCP 路径下 ontrack 可能早于 CONNECTED →
   * statsService 尚未绑定当前 PC 时 currentStats.keyframesDecoded 是上一连接残留值，
   * 处理＝延迟取：statsService 绑定到当前 PC 前不取/不比较 baseline，绑定后第一个 tick 再取。
   */
  private startKeyframeRetryUntilFrame(reason: string): void {
    this.keyframeRetryCount = 0;
    let baselineKf: number | null =
      statsService.attachedPc !== null && statsService.attachedPc === this.pc
        ? statsService.currentStats.keyframesDecoded
        : null;
    const maxRetries = 15, intervalMs = 200;                          // 3s 上限，可调
    const stop = () => { if (this.keyframeRetryTimer !== null) { clearInterval(this.keyframeRetryTimer); this.keyframeRetryTimer = null; } };
    stop();
    this.requestKeyframe();  // 立即先发一次
    this.keyframeRetryTimer = window.setInterval(() => {
      if (baselineKf === null) {
        if (statsService.attachedPc !== null && statsService.attachedPc === this.pc) {
          baselineKf = statsService.currentStats.keyframesDecoded;  // 延迟取 baseline（本 tick 不比较）
        }
      } else if (statsService.currentStats.keyframesDecoded > baselineKf) {
        this._log(`KeyframeRetry done (${reason}) after ${this.keyframeRetryCount} retries`);
        stop(); return;  // 收到 IDR，停
      }
      if (this.keyframeRetryCount >= maxRetries) { this._warn(`KeyframeRetry give up (${reason})`); stop(); return; }
      this.keyframeRetryCount++;
      this.requestKeyframe();
    }, intervalMs);
  }

  /**
   * Send video configuration to server (bitrate, fps, encoder params)
   */
  sendVideoConfig(config: {
    bitrate?: number;
    fps?: number;
    gop?: number;
    preset?: string;
    tuningInfo?: string;
    rateControl?: string;
    bFrames?: number;
    lookahead?: number;
  }): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({
        type: 'video_config',
        ...config
      }));
      this._log('Sent video config:', config);
    } else {
      this._warn('Cannot send video config - dataChannel not open, state:', this.dataChannel?.readyState);
      eventBus.emit('ui:toast', { message: '配置通道未就绪，请稍后重试', type: 'warning' });
    }
  }

  /**
   * Send fixed bitrate mode config to server (toggle / kbps)
   * host 收到后会持久化到 host_config.json 并广播给所有客户端
   */
  sendFixedBitrateConfig(enabled: boolean, nvencKbps: number, swKbps: number): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({
        type: 'fixed_bitrate_config',
        enabled,
        nvencKbps,
        swKbps,
      }));
      this._log('Sent fixed_bitrate_config:', { enabled, nvencKbps, swKbps });
    } else {
      this._warn('Cannot send fixed_bitrate_config - dataChannel not open, state:', this.dataChannel?.readyState);
      eventBus.emit('ui:toast', { message: '配置通道未就绪，请稍后重试', type: 'warning' });
    }
  }

  /**
   * 下载流控配置：通知 host 端文件流通道阈值（影响 file-data 通道 buffered_amount 上限）
   * host 端 atomic 实时生效，无需重连
   */
  sendDownloadConfig(windowKB: number): boolean {
    if (this.dataChannel?.readyState !== 'open') {
      this._warn('Cannot send download_config - dataChannel not open, state:', this.dataChannel?.readyState);
      return false;
    }
    this.dataChannel.send(JSON.stringify({ type: 'download_config', windowKB }));
    this._log('Sent download_config:', { windowKB });
    return true;
  }

  /**
   * Send screen capturer override to server.
   * userOverride=true 时 host 跳过 benchmark，直接用 preferred；false 时让 host 仍按 benchmark 决策。
   */
  sendScreenCapturerOverride(userOverride: boolean, preferred: 'auto' | 'wgc' | 'dxgi' | 'gdi'): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({
        type: 'screen_capturer_set_override',
        userOverride,
        preferred,
      }));
      this._log('Sent screen_capturer_set_override:', { userOverride, preferred });
    } else {
      this._warn('Cannot send screen_capturer_set_override - dataChannel not open, state:', this.dataChannel?.readyState);
      eventBus.emit('ui:toast', { message: '配置通道未就绪，请稍后重试', type: 'warning' });
    }
  }

  /**
   * 查询内网 TURN 当前状态 —— 走 signaling，host 返回 internal_turn_status
   * 结果通过 eventBus 'internal_turn:status' 事件分发，调用方监听即可。
   * 用 signaling 而非 DataChannel：SettingsPanel 打开时即使 race 没成功也能查。
   */
  queryInternalTurn(): void {
    if (!signalingService.isConnected) {
      this._warn('Cannot query internal turn - signaling not connected');
      return;
    }
    signalingService.send({ type: 'internal_turn_query' } as never);
  }

  /**
   * 触发 host 重新测速内网 TURN
   *   targets 为空 → 重测全部已知 endpoint
   *   targets 非空 → 只重测指定的（适合"单条测速"按钮）
   * Host 重测完后会推 internal_turn_status，通过同一事件分发结果。
   */
  probeInternalTurn(targets: Array<{ ip: string; port: number }> = []): void {
    if (!signalingService.isConnected) {
      this._warn('Cannot probe internal turn - signaling not connected');
      return;
    }
    signalingService.send({
      type: 'internal_turn_probe',
      targets,
    } as never);
  }

  /**
   * 触发 host 重新发现内网 TURN —— 重读 Config.ini + 重调 /api/getip + 重测。
   * 跟 probeInternalTurn 的区别：probe 仅刷新已知 endpoint 的 RTT；
   * rediscover 会把整个 endpoint 列表替换为接口最新返回的内容（新增/下线都生效）。
   * Host 完成后推 internal_turn_status，复用同一事件分发结果。
   */
  rediscoverInternalTurn(): void {
    if (!signalingService.isConnected) {
      this._warn('Cannot rediscover internal turn - signaling not connected');
      return;
    }
    signalingService.send({ type: 'internal_turn_rediscover' } as never);
  }

  /**
   * 编码器切换（阶段 2）
   * encoderId: 'nvenc_h264' | 'x264_h264' | 'openh264_h264' | ...
   * host 收到后切换全局 active encoder，断开所有 session 触发重连。
   * 重连完成后 host 用新 active 路由编码器。
   */
  sendSelectEncoder(encoderId: string): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({
        type: 'select_encoder',
        encoder_id: encoderId,
      }));
      this._log('Sent select_encoder:', encoderId);
    } else {
      this._warn('Cannot send select_encoder - dataChannel not open');
      eventBus.emit('ui:toast', { message: '配置通道未就绪，请稍后重试', type: 'warning' });
    }
  }

  /**
   * 触发 host 重新跑编码器自检（阶段 2）
   * 强制重测所有 6 个编码器，结果写回 encoder_cache.json + 推送新 catalog。
   * Host 端有并发保护，重复请求会回 already_running ack。
   */
  sendProbeReRun(): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({ type: 'probe_rerun' }));
      this._log('Sent probe_rerun');
    } else {
      this._warn('Cannot send probe_rerun - dataChannel not open');
      eventBus.emit('ui:toast', { message: '配置通道未就绪，请稍后重试', type: 'warning' });
    }
  }

  /**
   * 触发 host 重新跑截图器 benchmark（同步阻塞约 200~500ms，host 跑完会广播新 server_config_status）
   */
  sendScreenCapturerRebench(): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({ type: 'screen_capturer_rebench' }));
      this._log('Sent screen_capturer_rebench');
    } else {
      this._warn('Cannot send screen_capturer_rebench - dataChannel not open');
      eventBus.emit('ui:toast', { message: '配置通道未就绪，请稍后重试', type: 'warning' });
    }
  }

  /**
   * Send resolution config to server (change remote display resolution)
   */
  sendResolutionConfig(width: number, height: number): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({
        type: 'resolution_config',
        width,
        height
      }));
      this._log('Sent resolution config:', width, 'x', height);
    }
  }

  /**
   * Send stats collection config to server
   */
  sendStatsConfig(enabled: boolean): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({
        type: 'stats_config',
        enabled: enabled
      }));
      this._log('Sent stats config:', enabled);
    }
  }

  /**
   * Send audio config to server
   */
  sendAudioConfig(systemEnabled: boolean, micEnabled: boolean): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({
        type: 'audio_config',
        systemEnabled: systemEnabled,
        micEnabled: micEnabled
      }));
      this._log('Sent audio config: system=' + systemEnabled + ', mic=' + micEnabled);
    }
  }

  /**
   * Send visibility state to server (for pause/resume audio+video)
   */
  private sendVisibilityConfig(visible: boolean): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({
        type: 'visibility',
        visible: visible
      }));
      this._log('Sent visibility config: visible=' + visible);
    }
  }

  /**
   * Start monitoring page visibility for pause/resume
   * When tab is hidden for >10s, pause audio/video to save bandwidth
   */
  private startVisibilityMonitor(): void {
    if (this.boundVisibilityHandler) {
      return; // Already monitoring
    }

    this.boundVisibilityHandler = () => {
      if (document.hidden) {
        // Tab hidden - start 3s timer
        if (this.visibilityPauseTimer === null) {
          this._log('Tab hidden - starting 3s pause timer');
          this.visibilityPauseTimer = window.setTimeout(() => {
            this.visibilityPaused = true;
            this.sendVisibilityConfig(false);
            this._log('Tab hidden >3s - pausing audio/video');
          }, 3000);
        }
      } else {
        // Tab visible - cancel timer and resume if paused
        if (this.visibilityPauseTimer !== null) {
          window.clearTimeout(this.visibilityPauseTimer);
          this.visibilityPauseTimer = null;
          this._log('Tab visible - cancelled pause timer');
        }
        if (this.visibilityPaused) {
          this.visibilityPaused = false;
          this.sendVisibilityConfig(true);
          this._log('Tab visible - resuming audio/video');
        }
      }
    };

    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
    this._log('Visibility monitor started');
  }

  /**
   * Stop monitoring page visibility
   */
  private stopVisibilityMonitor(): void {
    if (this.boundVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
      this.boundVisibilityHandler = null;
    }
    if (this.visibilityPauseTimer !== null) {
      window.clearTimeout(this.visibilityPauseTimer);
      this.visibilityPauseTimer = null;
    }
    this.visibilityPaused = false;
  }

  /**
   * Idle detection: user inactive → warning countdown → pause
   */
  private startIdleMonitor(): void {
    if (this.boundIdleHandler) return;

    const resetIdle = () => {
      // 如果已暂停，不响应普通操作（需要点击恢复按钮）
      if (this.idlePaused) return;

      // 清除 idle 计时器
      if (this.idleTimer !== null) {
        window.clearTimeout(this.idleTimer);
      }
      // 清除倒计时
      if (this.idleCountdownTimer !== null) {
        window.clearInterval(this.idleCountdownTimer);
        this.idleCountdownTimer = null;
      }
      // 如果处于 warning 状态，恢复为 active
      if (this.idleCountdown > 0) {
        this.idleCountdown = 0;
        eventBus.emit('idle:stateChange', { state: 'active' });
        this.scheduleIdleUnblock();
      }

      // 重新开始 idle 计时
      this.idleTimer = window.setTimeout(() => {
        this.startIdleCountdown();
      }, this.IDLE_TIMEOUT);
    };

    this.boundIdleHandler = resetIdle;
    window.addEventListener('mousedown', resetIdle);
    window.addEventListener('keydown', resetIdle);
    window.addEventListener('wheel', resetIdle);

    // 初始启动 idle 计时
    this.idleTimer = window.setTimeout(() => {
      this.startIdleCountdown();
    }, this.IDLE_TIMEOUT);

    this._log('Idle monitor started (timeout=' + this.IDLE_TIMEOUT + 'ms)');
  }

  private scheduleIdleUnblock(): void {
    if (this.idleBlockingTimer !== null) {
      window.clearTimeout(this.idleBlockingTimer);
    }
    this.idleBlockingTimer = window.setTimeout(() => {
      this._idleBlocking = false;
      this.idleBlockingTimer = null;
    }, 100);
  }

  private startIdleCountdown(): void {
    this._idleBlocking = true;
    this.idleCountdown = this.IDLE_COUNTDOWN;
    eventBus.emit('idle:stateChange', { state: 'warning', countdown: this.idleCountdown });
    this._log('Idle warning - countdown ' + this.IDLE_COUNTDOWN + 's');

    this.idleCountdownTimer = window.setInterval(() => {
      this.idleCountdown--;
      if (this.idleCountdown <= 0) {
        // 倒计时结束，暂停
        if (this.idleCountdownTimer !== null) {
          window.clearInterval(this.idleCountdownTimer);
          this.idleCountdownTimer = null;
        }
        this.idlePaused = true;
        this.sendVisibilityConfig(false);
        eventBus.emit('idle:stateChange', { state: 'paused' });
        this._log('Idle paused - sent pause to host');
      } else {
        eventBus.emit('idle:stateChange', { state: 'warning', countdown: this.idleCountdown });
      }
    }, 1000);
  }

  /**
   * Resume from idle pause (called by UI resume button)
   */
  resumeFromIdle(): void {
    if (!this.idlePaused) return;
    this.idlePaused = false;
    this.sendVisibilityConfig(true);
    eventBus.emit('idle:stateChange', { state: 'active' });
    this.scheduleIdleUnblock();
    this._log('Idle resumed by user');

    // 重新开始 idle 计时
    if (this.idleTimer !== null) {
      window.clearTimeout(this.idleTimer);
    }
    this.idleTimer = window.setTimeout(() => {
      this.startIdleCountdown();
    }, this.IDLE_TIMEOUT);
  }

  private stopIdleMonitor(): void {
    if (this.boundIdleHandler) {
      window.removeEventListener('mousedown', this.boundIdleHandler);
      window.removeEventListener('keydown', this.boundIdleHandler);
      window.removeEventListener('wheel', this.boundIdleHandler);
      this.boundIdleHandler = null;
    }
    if (this.idleTimer !== null) {
      window.clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.idleCountdownTimer !== null) {
      window.clearInterval(this.idleCountdownTimer);
      this.idleCountdownTimer = null;
    }
    if (this.idleCountdown > 0 || this.idlePaused) {
      eventBus.emit('idle:stateChange', { state: 'active' });
    }
    this.idleCountdown = 0;
    this.idlePaused = false;
    this._idleBlocking = false;
    if (this.idleBlockingTimer !== null) {
      window.clearTimeout(this.idleBlockingTimer);
      this.idleBlockingTimer = null;
    }
  }

  /**
   * Send switch mode command to server (capture mode + encoder)
   * modeId: e.g., "dxgi_nvenc", "gdi_mf", "dxgi_mf_cpu"
   * Returns true if DataChannel was open and message was sent
   */
  sendSwitchMode(modeId: string): boolean {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({
        type: 'switch_mode',
        mode_id: modeId
      }));
      this._log('Sent switch mode via DataChannel:', modeId);
      return true;
    }
    this._warn('Cannot send switch mode - dataChannel not open');
    return false;
  }

  /**
   * Send jitter buffer value to server for adaptive bitrate
   */
  private sendJitterBuffer({ jitterBuffer }: { jitterBuffer: number }): void {
    if (this.dataChannel?.readyState === 'open' && jitterBuffer > 0) {
      this.dataChannel.send(JSON.stringify({
        type: 'jitter_buffer',
        value: Math.round(jitterBuffer)
      }));
    }
  }

  /**
   * Send network stats to server for adaptive FPS control
   */
  private sendNetworkStats(stats: {
    rtt: number;
    jitter: number;
    packetLoss: number;
    jitterBuffer: number;
    bitrate: number;
  }): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({
        type: 'network_stats',
        rtt: Math.round(stats.rtt),
        jitter: Math.round(stats.jitter * 10) / 10,        // 1 decimal
        packetLoss: Math.round(stats.packetLoss * 100) / 100, // 2 decimals
        jitterBuffer: Math.round(stats.jitterBuffer),
        bitrate: Math.round(stats.bitrate),
      }));
    }
  }

  /**
   * Get remote endpoint (IP:port) from WebRTC stats
   * Returns { endpoint, isRelay } for both display and connection type detection
   */
  private async getRemoteEndpoint(): Promise<{ endpoint: string; isRelay: boolean } | null> {
    if (!this.pc) return null;

    try {
      const stats = await this.pc.getStats();
      let remoteIp = '';
      let remotePort = 0;
      let remoteCandidateType = '';
      let localCandidateType = '';

      stats.forEach((report) => {
        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          const remoteCandidateId = report.remoteCandidateId;
          const localCandidateId = report.localCandidateId;
          stats.forEach((r) => {
            if (r.id === remoteCandidateId && r.type === 'remote-candidate') {
              remoteIp = r.address || r.ip || '';
              remotePort = r.port || 0;
              remoteCandidateType = r.candidateType || '';
            }
            if (r.id === localCandidateId && r.type === 'local-candidate') {
              localCandidateType = r.candidateType || '';
            }
          });
        }
      });

      if (remoteIp && remotePort) {
        const typeStr = remoteCandidateType === 'relay' ? ' (TURN)' : remoteCandidateType === 'srflx' ? ' (STUN)' : '';
        const isRelay = localCandidateType === 'relay' || remoteCandidateType === 'relay';
        return { endpoint: `${remoteIp}:${remotePort}${typeStr}`, isRelay };
      }
    } catch {
      // Stats not available
    }
    return null;
  }

  /**
   * Handle incoming offer
   */
  private async handleOffer(offer: RTCSessionDescriptionInit): Promise<void> {
    const isConnected = this._state === ConnectionState.CONNECTED;
    const isRestart = this._state === ConnectionState.FAILED || this._state === ConnectionState.ICE_CONNECTING;
    this._log('handleOffer called, current state:', this._state,
      isConnected ? '(ICE Restart for P2P)' : isRestart ? '(ICE Restart)' : '');

    // Suppress toast in fast reconnect mode
    if (isRestart && !this.fastReconnectMode) {
      eventBus.emit('ui:toast', { message: '正在重新尝试连接...', type: 'info' });
    }

    try {
      // Always recreate PeerConnection for ICE restart (hudd4 behavior)
      this.createPeerConnection();
      await this.pc!.setRemoteDescription(offer);
      this.setState(ConnectionState.ICE_GATHERING);

      // Apply pending candidates
      for (const candidate of this.pendingRemoteCandidates) {
        await this.pc!.addIceCandidate(candidate);
      }
      this.pendingRemoteCandidates = [];

      // Create and send answer
      const answer = await this.pc!.createAnswer();
      await this.pc!.setLocalDescription(answer);
      signalingService.sendAnswer(answer.sdp!);

      // Send local candidates
      for (const candidate of this.localCandidates) {
        signalingService.sendCandidate(candidate);
      }
    } catch (e) {
      this._error('Failed to handle offer:', e);
      this.handleConnectionError(e as Error);
    }
  }

  /**
   * Handle incoming ICE candidate
   */
  private async handleRemoteCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    this._log('Remote ICE candidate:', candidate.candidate);

    if (!this.pc || !this.pc.remoteDescription) {
      this.pendingRemoteCandidates.push(candidate);
      this._log('Queued remote candidate (no remote description yet)');
      return;
    }

    try {
      await this.pc.addIceCandidate(candidate);
    } catch (e) {
      this._error('Failed to add ICE candidate:', e);
    }
  }

  /**
   * Get current peer ID for ICE caching
   */
  private getPeerId(): string {
    // Relay mode: use peerId from config
    if (this._relayConfig?.peerId) {
      return this._relayConfig.peerId;
    }
    // Direct mode: use wsUrl host as peer ID
    if (this.wsUrl) {
      try {
        const url = new URL(this.wsUrl);
        return url.host;
      } catch {
        return this.wsUrl;
      }
    }
    return 'unknown';
  }

  /**
   * Create RTCPeerConnection
   */
  private createPeerConnection(): void {
    this.cleanup();
    this.intentionalDisconnect = false; // 重置主动断开标志

    // Configure ICE servers and transport policy based on icePolicy
    let iceServers = [...this.config.iceServers];
    let iceTransportPolicy: RTCIceTransportPolicy = 'all';

    // If P2P assist found a working STUN server, use it exclusively
    if (this.p2pAssistStunServer) {
      this._log('Using P2P Assist STUN server:', this.p2pAssistStunServer);
      iceServers = [{ urls: this.p2pAssistStunServer }];
      iceTransportPolicy = 'all'; // Allow P2P candidates only
      // Clear the saved STUN after use (one-time switch)
      this.p2pAssistStunServer = null;
    } else {
      switch (this.config.icePolicy) {
        case 'p2p':
          // P2P only: remove TURN servers, keep only STUN
          iceServers = iceServers.filter((server) => {
            const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
            return !urls.some((u) => u.startsWith('turn:'));
          });
          this._log('ICE Policy: P2P only (STUN only)');
          break;
        case 'relay':
          // Relay only: force relay transport
          iceTransportPolicy = 'relay';
          this._log('ICE Policy: Relay only (TURN only)');
          break;
        case 'auto':
        default:
          // Auto: use all servers with default policy
          this._log('ICE Policy: Auto (P2P preferred, TURN fallback)');
          break;
      }
    }

    const rtcConfig: RTCConfiguration = {
      iceServers,
      iceTransportPolicy,
      // Pre-gather candidates for faster ICE negotiation
      // Higher value = more candidates ready when offer/answer exchange happens
      iceCandidatePoolSize: 10,
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
    };

    this._log('Creating PeerConnection with config:', {
      icePolicy: this.config.icePolicy,
      iceTransportPolicy: rtcConfig.iceTransportPolicy,
      iceServers: rtcConfig.iceServers?.map(s => s.urls)
    });

    this.pc = new RTCPeerConnection(rtcConfig);

    // Create data channel for general input (keyboard, clipboard, config)
    // 使用可靠模式确保消息不丢失
    // priority: high - 键盘输入需要低延迟
    this.dataChannel = this.pc.createDataChannel('input', {
      ordered: true,
      priority: 'high',
    });

    // Create dedicated mouse channel for lowest latency
    // ordered: false - 无序，避免队头阻塞（一个包丢失不会阻塞后续包）
    // maxRetransmits: 0 - 不重传，丢失的鼠标位置直接跳过
    // priority: high - 鼠标输入需要最低延迟
    this.mouseChannel = this.pc.createDataChannel('mouse', {
      ordered: false,
      maxRetransmits: 0,
      priority: 'high',
    });

    // Create dedicated fs-rpc channel for file manager JSON-RPC
    // 独立通道避免文件管理器操作阻塞远程桌面的键盘/光标
    // priority: medium - 文件系统操作不需要实时响应
    this.fsRpcChannel = this.pc.createDataChannel('fs-rpc', {
      ordered: true,
      priority: 'medium',
    });

    this.setupDataChannelEvents();
    this.setupMouseChannelEvents();
    this.setupFsRpcChannelEvents();
    this.setupPeerConnectionEvents();
  }

  /**
   * Setup data channel events
   */
  private setupDataChannelEvents(): void {
    if (!this.dataChannel) return;


    const onDataChannelReady = () => {
      this._log('DataChannel READY');

      // [TCP 修复 C] DC 真就绪才切到 CONNECTED + 弹 winner toast（来自 handleRaceWinner 的延迟通知）
      if (this.pendingWinnerToast) {
        this.setState(ConnectionState.CONNECTED);
        this.hasBeenConnected = true;
        this.reconnectAttempt = 0;
        eventBus.emit('ui:toast', {
          message: `${this.pendingWinnerToast.text} 连接已建立 (${this.pendingWinnerToast.ms}ms)`,
          type: 'success'
        });
        this.pendingWinnerToast = null;
        // 取消 8 秒 DC 兜底 timer
        if (this.dcReadyTimeoutTimer) {
          clearTimeout(this.dcReadyTimeoutTimer);
          this.dcReadyTimeoutTimer = null;
        }
      }

      eventBus.emit('webrtc:datachannel', this.dataChannel!);
      eventBus.emit('webrtc:dataChannelReady', true);  // Notify InputService

      // B5-step3：DC open 后改为周期请求关键帧直到首个 IDR 解码（成功即停）
      // 单一 keyframeRetryTimer 自然去重，与 ontrack 路不叠加
      setTimeout(() => { this.startKeyframeRetryUntilFrame('dc-open'); }, 100);

      // Start visibility monitor for pause/resume on tab hidden
      this.startVisibilityMonitor();

      // Start idle monitor for pause on user inactivity
      this.startIdleMonitor();
    };

    this.dataChannel.onopen = () => {
      this._log('DataChannel OPEN');
      onDataChannelReady();
    };

    // If already open (ICE Racing winner), trigger ready immediately
    if (this.dataChannel.readyState === 'open') {
      this._log('DataChannel already open (ICE Racing)');
      onDataChannelReady();
    }

    this.dataChannel.onclose = () => {
      this._log('DataChannel CLOSED');
      this.stopVisibilityMonitor();
      this.stopIdleMonitor();
      eventBus.emit('webrtc:dataChannelReady', false);  // Notify InputService
    };

    this.dataChannel.onerror = (e) => {
      this._error('DataChannel ERROR:', e);
    };

    this.dataChannel.onmessage = (event: MessageEvent) => {
      // Handle cursor updates, server stats, clipboard, etc.
      try {
        let text: string;
        const d = event.data;
        if (typeof d === 'string') {
          text = d;
        } else if (d?.byteLength !== undefined) {
          text = new TextDecoder().decode(d);
        } else {
          return;
        }

        const data = JSON.parse(text);
        // P2P Assist Sync 打洞控制消息 → 转发给 P2PAssistService 处理
        if (data.type === 'p2p_assist_ctrl') {
          eventBus.emit('p2p_assist:ctrl_recv', data);
          return;
        }
        if (data.type === 'cursor') {
          this._log('Cursor received:', data.cursor?.cursorCSS, 'type:', data.cursor?.cursorType, 'visible:', data.cursor?.visible);
          eventBus.emit('input:cursor', data);
        } else if (data.type === 'server_stats') {
          eventBus.emit('stats:rawServerStats', data.stats);
        } else if (data.type === 'server_caps') {
          // server_caps 是小消息（< 500 字节），承载 SettingsPanel 必需的能力字段
          // （encoder_type/av1_hw/cpu_cores/platform 等）。当 server_stats 因体积
          // 过大被 SCTP 静默丢弃时，server_caps 是 SettingsPanel 拿到能力字段的唯一来源。
          eventBus.emit('stats:serverCaps', data);
        } else if (data.type === 'server_encoder_catalog') {
          // 编码器自检 catalog（阶段 2）：独立小消息，存活 5s 心跳 + dirty 立即发
          eventBus.emit('stats:encoderCatalog', data);
        } else if (data.type === 'encoder_fallback') {
          // 运行期编码器降级通知（阶段 3 才会真触发，阶段 2 先建好接收兜底）
          eventBus.emit('encoder:fallback', data);
        } else if (data.type === 'select_encoder_ack') {
          eventBus.emit('encoder:selectAck', data);
        } else if (data.type === 'probe_rerun_ack') {
          eventBus.emit('encoder:probeRerunAck', data);
        } else if (data.type === 'stats') {
          // 简单统计消息：{type: 'stats', fps, bitrate (bps), clients, version}
          if (data.bitrate) {
            eventBus.emit('stats:encoderBitrate', { bitrate: data.bitrate });
          }
          if (data.version) {
            eventBus.emit('stats:hostVersion', { version: data.version });
          }
        } else if (data.type === 'clipboard') {
          // Check if it's a chunked message
          if (data.chunk !== undefined && data.total !== undefined) {
            // Chunked clipboard message
            if (data.chunk === 0) {
              // Start of new chunked transfer
              this.clipboardChunks.clear();
              this.clipboardTotalChunks = data.total;
            }
            this.clipboardChunks.set(data.chunk, data.text || '');

            // Check if all chunks received
            if (this.clipboardChunks.size === this.clipboardTotalChunks) {
              // Reassemble
              let fullText = '';
              for (let i = 0; i < this.clipboardTotalChunks; i++) {
                fullText += this.clipboardChunks.get(i) || '';
              }
              this.clipboardChunks.clear();
              this.clipboardTotalChunks = 0;
              console.log(`[WebRTC] Reassembled clipboard: ${fullText.length} chars`);
              eventBus.emit('clipboard:sync', fullText);
            }
          } else {
            // Non-chunked clipboard message
            eventBus.emit('clipboard:sync', data.text || '');
          }
        } else if (data.type === 'clipboard_query_result') {
          // 拉模式查询响应：requestId 配对 → ClipboardQueryService
          this._log('Received clipboard_query_result, requestId=', data.requestId, 'hasFiles=', data.hasFiles);
          clipboardQueryService.handleResult(data);
        } else if (data.type === 'agent_restart') {
          this._log('Received agent_restart notification');
          eventBus.emit('agent:restart');
        } else if (data.type === 'file_search_status') {
          // File search engine status update (pushed from backend)
          this._log('Received file_search_status:', data.status, 'progress:', data.progress);
          eventBus.emit('file_search:status', {
            status: data.status,
            progress: data.progress,
            current: data.current,
            total: data.total,
            phase: data.phase,
            message: data.message,
          });
        } else if (data.type === 'server_config_status') {
          // host 端推送的全局配置状态（连接建立 / 配置变更后广播）
          // 当前只有 fixedBitrate 一个字段，未来其他全局配置可挂这里
          this._log('Received server_config_status:', data);
          eventBus.emit('config:server_status', data);
        }
      } catch {
        // Ignore parse errors
      }
    };

    // 回放 IceRacing 阶段缓冲的早期消息（onmessage 注册前到达的 server_stats 等）
    // 由 IceRacingService 在 ondatachannel 'data' 分支挂的临时 onmessage 写入 __earlyBuffer。
    const dcWithBuf = this.dataChannel as RTCDataChannel & { __earlyBuffer?: MessageEvent[] };
    const earlyBuf = dcWithBuf.__earlyBuffer;
    if (earlyBuf && earlyBuf.length > 0) {
      const handler = this.dataChannel.onmessage;
      const dc = this.dataChannel;
      for (const ev of earlyBuf) {
        try {
          handler?.call(dc, ev);
        } catch (e) {
          console.warn('[DC-DIAG-RECV] replay handler threw:', e);
        }
      }
    }
    delete dcWithBuf.__earlyBuffer;

    // 主动请求一次 server_config_status：
    // host 在 OnStateChange[OPEN] 时确实推过一次，但那是在 IceRacing 阶段、winner 还没选定，
    // WebRTCService 此时还没把 winner 的 dataChannel 接管 + 注册 onmessage，那条消息被丢。
    // 在 onmessage 注册完成后主动请求，host 收到 request_server_config_status 立即回推。
    const sendConfigRequest = () => {
      if (this.dataChannel?.readyState === 'open') {
        try {
          this.dataChannel.send(JSON.stringify({ type: 'request_server_config_status' }));
          this._log('Sent request_server_config_status');
        } catch (e) {
          this._warn('Failed to send request_server_config_status:', e);
        }
      }
    };
    if (this.dataChannel.readyState === 'open') {
      sendConfigRequest();
    } else {
      // channel 还没 open，包装现有 onopen，open 触发后再请求
      const prevOnOpen = this.dataChannel.onopen;
      this.dataChannel.onopen = (ev) => {
        if (typeof prevOnOpen === 'function') {
          (prevOnOpen as (this: RTCDataChannel, ev: Event) => any).call(this.dataChannel!, ev);
        }
        sendConfigRequest();
      };
    }
  }

  /**
   * Setup mouse channel events (dedicated low-latency channel)
   */
  private setupMouseChannelEvents(): void {
    if (!this.mouseChannel) return;

    this.mouseChannel.onopen = () => {
      this._log('MouseChannel OPEN');
      eventBus.emit('webrtc:mouseChannel', this.mouseChannel!);
    };

    this.mouseChannel.onclose = () => {
      this._log('MouseChannel CLOSED');
    };

    this.mouseChannel.onerror = (e) => {
      this._error('MouseChannel ERROR:', e);
    };
  }

  /**
   * Setup fs-rpc channel events (dedicated channel for file manager JSON-RPC)
   */
  private setupFsRpcChannelEvents(): void {
    if (!this.fsRpcChannel) return;

    this.fsRpcChannel.onopen = () => {
      this._log('FsRpcChannel OPEN');
      eventBus.emit('webrtc:fsRpcChannel', this.fsRpcChannel!);
    };

    this.fsRpcChannel.onclose = () => {
      this._log('FsRpcChannel CLOSED');
    };

    this.fsRpcChannel.onerror = (e) => {
      this._error('FsRpcChannel ERROR:', e);
    };

    // Note: onmessage is set by WebRTCSystem when it calls setDataChannel
  }

  /**
   * Lazily create fs-rpc channel on the current active PC.
   * Called when FileManager opens — works after any connection switch.
   */
  async ensureFsRpcChannel(): Promise<RTCDataChannel | null> {
    if (this.fsRpcChannel && this.fsRpcChannel.readyState === 'open') {
      return this.fsRpcChannel;
    }
    // 清空已关闭或不可用的旧通道引用
    if (this.fsRpcChannel && this.fsRpcChannel.readyState !== 'open') {
      this.fsRpcChannel = null;
    }
    if (!this.pc || this.pc.connectionState !== 'connected') {
      this._warn('ensureFsRpcChannel: PC not connected');
      return null;
    }

    this._log('ensureFsRpcChannel: creating fs-rpc on current PC...');
    this.fsRpcChannel = this.pc.createDataChannel('fs-rpc', {
      ordered: true,
      priority: 'medium',
    });
    this.setupFsRpcChannelEvents();

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this._warn('ensureFsRpcChannel: timeout waiting for open');
        resolve(null);
      }, 5000);

      if (this.fsRpcChannel!.readyState === 'open') {
        clearTimeout(timeout);
        resolve(this.fsRpcChannel);
        return;
      }
      this.fsRpcChannel!.addEventListener('open', () => {
        clearTimeout(timeout);
        resolve(this.fsRpcChannel);
      }, { once: true });
    });
  }

  /**
   * Setup peer connection events
   */
  private setupPeerConnectionEvents(): void {
    if (!this.pc) return;

    this.pc.onicecandidate = (event) => {
      if (event.candidate) {
        const candidateInit = event.candidate.toJSON();
        const candidateStr = event.candidate.candidate;
        const isRelay = candidateStr.includes('typ relay');
        this._log('Local ICE candidate:', candidateStr, isRelay ? '(relay)' : '(p2p)');

        // Filter out unwanted candidates
        if (this.shouldFilterCandidate(candidateInit)) {
          this._log('Filtered candidate (virtual network)');
          return;
        }

        this.localCandidates.push(candidateInit);

        // Send candidate - delay relay candidates 3s to prioritize P2P
        const sendCandidate = () => {
          if (this.pc?.localDescription) {
            // Skip relay if already connected via P2P
            if (isRelay && this.pc?.connectionState === 'connected') {
              this._log('Already connected via P2P, skipping relay candidate');
              return;
            }
            signalingService.sendCandidate(candidateInit);
          }
        };

        if (isRelay) {
          this._log('Delaying relay candidate by 2s (P2P priority)');
          setTimeout(sendCandidate, 2000);
        } else {
          sendCandidate();
        }
      } else {
        this._log('ICE gathering complete');
      }
    };

    // Timer for handling temporary disconnects
    let disconnectTimer: number | null = null;
    let previousIceState: RTCIceConnectionState | undefined;

    this.pc.oniceconnectionstatechange = () => {
      const state = this.pc?.iceConnectionState;
      const connState = this.pc?.connectionState;
      this._log('ICE State:', state, '| Connection State:', connState, '| Signaling:', this.pc?.signalingState);
      eventBus.emit('webrtc:iceState', state as RTCIceConnectionState);

      // Clear disconnect timer if state changes
      if (disconnectTimer) {
        clearTimeout(disconnectTimer);
        disconnectTimer = null;
      }

      switch (state) {
        case 'checking':
          this.setState(ConnectionState.ICE_CONNECTING);
          // Suppress toast in fast reconnect mode
          // Note: Don't show connection type here - we don't know actual type until ICE succeeds
          if (!this.fastReconnectMode) {
            eventBus.emit('ui:toast', { message: '正在建立连接...', type: 'info' });
          }
          break;
        case 'connected':
        case 'completed':
          this.setState(ConnectionState.CONNECTED);
          this.hasBeenConnected = true;
          this.reconnectAttempt = 0;

          // P2P established - keep relay WebSocket connected
          // (Host needs to know Client is still alive)
          if (signalingService.isRelayMode) {
            this._log('P2P established via relay mode');
          }

          // Exit fast reconnect mode if active
          const wasFastReconnect = this.fastReconnectMode;
          if (this.fastReconnectMode) {
            this._log('Fast reconnect successful, exiting fast reconnect mode');
            this.fastReconnectMode = false;
          }

          // Get remote candidate info and show toast
          this.getRemoteEndpoint().then((result) => {
            if (result) {
              // Show toast (unless fast reconnect)
              if (!wasFastReconnect) {
                const connTypeText = result.isRelay ? '中转' : 'P2P';
                eventBus.emit('ui:toast', {
                  message: `${connTypeText} 连接已建立 - ${result.endpoint}`,
                  type: 'success'
                });
              }
            } else if (!wasFastReconnect) {
              // Fallback to config when can't detect actual type
              const fallbackType = this.config.icePolicy === 'relay' ? '中转' : 'P2P';
              eventBus.emit('ui:toast', { message: `${fallbackType} 连接已建立`, type: 'success' });
            }
          });

          if (wasFastReconnect) {
            this._log('Suppressed connection toast (fast reconnect)');
          }

          // Request keyframe when recovering from disconnected state
          if (previousIceState === 'disconnected') {
            this.requestKeyframe();
          }
          break;
        case 'disconnected':
          // ICE disconnected can be temporary, wait 5 seconds before reconnecting
          // Suppress toast in fast reconnect mode
          if (!this.fastReconnectMode) {
            eventBus.emit('ui:toast', { message: '连接不稳定，等待恢复...', type: 'warning' });
          }

          disconnectTimer = window.setTimeout(() => {
            if (this.pc?.iceConnectionState === 'disconnected' && this.hasBeenConnected) {
              // 5秒后仍然disconnected，打印错误日志
              if (!this.intentionalDisconnect) {
                logConnectionError('ICE连接断开超过5秒未恢复', {
                  previousState: previousIceState,
                  connectionState: this.pc?.connectionState,
                  signalingState: this.pc?.signalingState,
                  hasBeenConnected: this.hasBeenConnected,
                });
              }
              this.scheduleReconnect(true);
            }
          }, 5000);
          break;
        case 'failed':
          // 打印错误日志（非主动断开时）
          if (!this.intentionalDisconnect) {
            logConnectionError('ICE连接失败', {
              previousState: previousIceState,
              connectionState: this.pc?.connectionState,
              signalingState: this.pc?.signalingState,
              hasBeenConnected: this.hasBeenConnected,
            });
          }
          // Suppress toast in fast reconnect mode
          if (!this.fastReconnectMode) {
            eventBus.emit('ui:toast', { message: 'P2P 连接失败', type: 'error' });
          }
          if (this.hasBeenConnected) {
            this.handleConnectionError(new Error('ICE connection failed'));
          } else {
            this.handleInitialConnectionError(new Error('ICE connection failed'));
          }
          break;
        case 'closed':
          // 打印错误日志（非主动断开时）
          if (!this.intentionalDisconnect && this.hasBeenConnected) {
            logConnectionError('ICE连接被关闭', {
              previousState: previousIceState,
              hasBeenConnected: this.hasBeenConnected,
            });
          }
          // Host 主动关闭 PeerConnection（例如编码器切换）会让本端 ICE 进入 closed。
          // 此前的实现只 setState DISCONNECTED 不重连，导致编码器切换后断开就停了。
          // 改为：非用户主动断开 + 之前连过 → 进入 fastReconnect 模式
          // （200ms 间隔不停重试 5 秒，比普通 scheduleReconnect 1 秒退避起步更快）
          if (!this.intentionalDisconnect && this.hasBeenConnected) {
            this._log('PC closed by remote — entering fast reconnect mode');
            this.fastReconnectMode = true;
            this.fastReconnectEndTime = Date.now() + 5000;
            this.stopReconnect();
            // B5-step4：清理关键帧周期重发定时器
            if (this.keyframeRetryTimer !== null) {
              clearInterval(this.keyframeRetryTimer);
              this.keyframeRetryTimer = null;
            }
            this.cleanup();
            this._state = ConnectionState.DISCONNECTED;
            this.reconnectAttempt = 0;
            this.scheduleFastReconnect();
          } else {
            this.setState(ConnectionState.DISCONNECTED);
          }
          break;
      }

      previousIceState = state;
    };

    this.pc.ontrack = (event) => {
      eventBus.emit('webrtc:track', event.track);
      this._log('ontrack:', event.track.kind, 'id:', event.track.id, 'readyState:', event.track.readyState, 'muted:', event.track.muted);

      if (event.track.kind === 'video') {
        this._remoteStream = event.streams[0] || new MediaStream([event.track]);
        this._log('Video track received, streams:', event.streams.length, 'track.muted:', event.track.muted);

        if (this.videoElement) {
          this.videoElement.srcObject = this._remoteStream;

          // Set ultra-low latency - use jitterBufferTarget (newer API) or playoutDelayHint
          if (event.receiver) {
            // jitterBufferTarget: target jitter buffer duration in SECONDS (W3C spec)
            // 0 = minimize jitter buffer as much as possible (cloud gaming)
            if ('jitterBufferTarget' in event.receiver) {
              (event.receiver as unknown as { jitterBufferTarget: number | null }).jitterBufferTarget = 0;
              this._log('Set jitterBufferTarget = 0 (minimize)');
            }
            // playoutDelayHint: hint for playout delay in seconds
            if ('playoutDelayHint' in event.receiver) {
              (event.receiver as unknown as { playoutDelayHint: number }).playoutDelayHint = 0;
              this._log('Set playoutDelayHint = 0');
            }
          }

          this.videoElement.play().catch((e) => {
            this._log('Video play failed:', e.message);
            eventBus.emit('ui:toast', { message: '点击屏幕播放视频', type: 'info' });
          });

          // B5-step5（OQ-4 核实结论）：直连/ICE-restart 主路径真实存在（SignalingService 普通
          // offer（无 raceId）→ handleOffer → createPeerConnection → 本 ontrack 收 video），
          // 该路径同样周期请求关键帧直到首个 IDR 解码（成功即停）
          this.startKeyframeRetryUntilFrame('main-ontrack');

          // Track events - only show toast after connection is established
          event.track.onmute = () => {
            this._log('Track MUTED - readyState:', event.track.readyState, 'enabled:', event.track.enabled);
            if (this.hasBeenConnected) {
              eventBus.emit('ui:toast', { message: '视频流已暂停', type: 'warning' });
            }
          };

          event.track.onunmute = () => {
            this._log('Track UNMUTED - readyState:', event.track.readyState, 'enabled:', event.track.enabled);
            if (this.hasBeenConnected) {
              eventBus.emit('ui:toast', { message: '视频流已恢复', type: 'success' });
              // Request keyframe when video resumes
              this.requestKeyframe();
            }
          };

          event.track.onended = () => {
            this._log('Track ENDED - readyState:', event.track.readyState);
            if (this.hasBeenConnected) {
              eventBus.emit('ui:toast', { message: '视频流已结束', type: 'warning' });
            }
          };
        } else {
          this._error('No video element!');
        }
      } else if (event.track.kind === 'audio') {
        // Handle audio track
        const audioStream = event.streams[0] || new MediaStream([event.track]);
        const trackId = event.track.id || '';

        this._log('Audio track received:', trackId, 'label:', event.track.label);

        // Determine audio source by track ID
        if (trackId.includes('system')) {
          eventBus.emit('audio:systemTrack', audioStream);
          this._log('System audio track ready');
        } else if (trackId.includes('mic')) {
          eventBus.emit('audio:micTrack', audioStream);
          this._log('Microphone track ready');
        } else {
          // Unknown audio track, treat as system audio
          eventBus.emit('audio:systemTrack', audioStream);
          this._log('Unknown audio track, treating as system audio');
        }
      }
    };

    this.pc.ondatachannel = (event) => {
      const channel = event.channel;
      this._log('Received DataChannel:', channel.label);

      // Host 创建的 file-ctrl/file-data 通道 → 转发给 FileTransferService
      if (channel.label === 'file-ctrl' || channel.label === 'file-data') {
        this._log('Host-created file channel received:', channel.label);
        eventBus.emit('fileTransfer:hostChannel', { label: channel.label, channel });
        return;
      }

      channel.onmessage = (e) => {
        try {
          let text: string;
          const d = e.data;
          if (typeof d === 'string') {
            text = d;
          } else if (d?.byteLength !== undefined) {
            text = new TextDecoder().decode(d);
          } else {
            return;
          }

          const data = JSON.parse(text);
          if (data.type === 'cursor') {
            this._log('Cursor received (ondatachannel):', data.cursor?.cursorCSS, 'type:', data.cursor?.cursorType, 'visible:', data.cursor?.visible);
            eventBus.emit('input:cursor', data);
          } else if (data.type === 'server_stats') {
            eventBus.emit('stats:rawServerStats', data.stats);
          } else if (data.type === 'server_caps') {
            eventBus.emit('stats:serverCaps', data);
          } else if (data.type === 'server_encoder_catalog') {
            eventBus.emit('stats:encoderCatalog', data);
          } else if (data.type === 'encoder_fallback') {
            eventBus.emit('encoder:fallback', data);
          } else if (data.type === 'select_encoder_ack') {
            eventBus.emit('encoder:selectAck', data);
          } else if (data.type === 'probe_rerun_ack') {
            eventBus.emit('encoder:probeRerunAck', data);
          } else if (data.type === 'clipboard') {
            // Check if it's a chunked message
            if (data.chunk !== undefined && data.total !== undefined) {
              // Chunked clipboard message
              if (data.chunk === 0) {
                this.clipboardChunks.clear();
                this.clipboardTotalChunks = data.total;
              }
              this.clipboardChunks.set(data.chunk, data.text || '');

              if (this.clipboardChunks.size === this.clipboardTotalChunks) {
                let fullText = '';
                for (let i = 0; i < this.clipboardTotalChunks; i++) {
                  fullText += this.clipboardChunks.get(i) || '';
                }
                this.clipboardChunks.clear();
                this.clipboardTotalChunks = 0;
                console.log(`[WebRTC] Reassembled clipboard: ${fullText.length} chars (ondatachannel)`);
                eventBus.emit('clipboard:sync', fullText);
              }
            } else {
              eventBus.emit('clipboard:sync', data.text || '');
            }
          } else if (data.type === 'clipboard_query_result') {
            // 拉模式查询响应
            this._log('Received clipboard_query_result via ondatachannel, requestId=', data.requestId);
            clipboardQueryService.handleResult(data);
          } else if (data.type === 'agent_restart') {
            this._log('Received agent_restart notification (ondatachannel)');
            eventBus.emit('agent:restart');
          }
        } catch {
          // Ignore parse errors
        }
      };
    };
  }

  /**
   * Filter unwanted ICE candidates
   */
  private shouldFilterCandidate(candidate: RTCIceCandidateInit): boolean {
    const candidateStr = candidate.candidate || '';

    // Allow host candidates for better P2P connectivity

    // Filter link-local addresses
    if (candidateStr.includes('169.254.')) return true;

    // Filter docker/virtual network addresses
    if (candidateStr.includes('172.17.')) return true;
    if (candidateStr.includes('172.18.')) return true;

    return false;
  }

  /**
   * Handle agent restart notification
   * Enters fast reconnect mode: 5 seconds of unlimited reconnects with no toast
   */
  private handleAgentRestart(): void {
    this._log('Agent restart notification received, entering fast reconnect mode');

    // Enter fast reconnect mode for 5 seconds
    this.fastReconnectMode = true;
    this.fastReconnectEndTime = Date.now() + 5000;

    // Clean up current connection immediately
    this.stopReconnect();
    this.cleanup();
    signalingService.disconnect();

    // Reset state but preserve wsUrl
    this._state = ConnectionState.DISCONNECTED;
    this.reconnectAttempt = 0;

    // Start fast reconnect immediately
    this.scheduleFastReconnect();
  }

  /**
   * Schedule fast reconnect (for agent restart scenario)
   * Retries every 200ms for 5 seconds with no toast notifications
   */
  private scheduleFastReconnect(): void {
    // Check if still in fast reconnect window
    if (!this.fastReconnectMode || Date.now() >= this.fastReconnectEndTime) {
      this._log('Fast reconnect window expired');
      this.fastReconnectMode = false;
      // Fall back to normal reconnect with toast
      eventBus.emit('ui:toast', { message: '服务器重连超时，请检查服务器状态', type: 'warning' });
      this.setState(ConnectionState.FAILED);
      return;
    }

    // Check if already connected
    if (this._state === ConnectionState.CONNECTED) {
      this._log('Fast reconnect: already connected');
      this.fastReconnectMode = false;
      return;
    }

    // Check if we have wsUrl or relayConfig
    if (!this.wsUrl && !this._relayConfig) {
      this._error('Fast reconnect: no wsUrl or relayConfig');
      this.fastReconnectMode = false;
      return;
    }

    this.reconnectAttempt++;
    this._log('Fast reconnect attempt', this.reconnectAttempt, '(silent)');

    // Set state to reconnecting (UI may show spinner but no toast)
    this.setState(ConnectionState.RECONNECTING);

    // Try to connect
    this.cleanup();
    const connectPromise = this._relayConfig
      ? this.connectRelay(this._relayConfig)
      : this.connect(this.wsUrl);

    connectPromise.catch(() => {
      // Schedule next attempt after 200ms
      this.reconnectTimer = window.setTimeout(() => {
        this.reconnectTimer = null;
        this.scheduleFastReconnect();
      }, 200);
    });
  }

  /**
   * Handle signaling close
   */
  private handleSignalingClose(): void {
    this._log('Signaling closed, state:', this._state, 'hasBeenConnected:', this.hasBeenConnected, 'fastReconnect:', this.fastReconnectMode, 'waitingForHost:', signalingService.waitingForHost);

    // If waiting for host to come online, ignore close event (server closes connection after host_offline error)
    // SignalingService will retry every second
    if (signalingService.waitingForHost) {
      // host 重启/离线时信令会自动重连；此时旧 race 的 session 已随 WS 断开失效。
      // 必须取消残留 race，否则重连后 host online 触发的 startRace 会被
      // IceRacingService 的 "Already racing" 守卫拦截，导致永久连不上（需刷新页面才能恢复）。
      // cancel() 内部首行 if (!isRacing) return，对非 racing 状态是 no-op，首连安全。
      if (iceRacingService.racing) {
        iceRacingService.cancel();
        this._log('Waiting for host, ignoring signaling close (cancelled stale race for clean reconnect)');
      } else {
        this._log('Waiting for host, ignoring signaling close');
      }
      return;
    }

    // In fast reconnect mode, let scheduleFastReconnect handle retries
    if (this.fastReconnectMode) {
      this._log('Signaling closed during fast reconnect, will retry');
      return;
    }

    if (this._state === ConnectionState.CONNECTED) {
      // Connection still alive via ICE, no action needed
      this._log('ICE connection still active, ignoring signaling close');
    } else if (this._state === ConnectionState.ICE_GATHERING || this._state === ConnectionState.ICE_CONNECTING) {
      // ICE is in progress, don't fail yet - let ICE complete
      this._log('ICE in progress, waiting for completion');
    } else if (this._state !== ConnectionState.DISCONNECTED) {
      if (this.hasBeenConnected) {
        this._log('Was connected before, scheduling reconnect');
        this.scheduleReconnect(false);
      } else if (this._state === ConnectionState.CONNECTING || this._state === ConnectionState.SIGNALING) {
        this.handleInitialConnectionError(new Error('Signaling connection closed'));
      }
    }
  }

  /**
   * Handle connection error (for reconnects after successful connection)
   */
  private handleConnectionError(error: Error): void {

    // Only reconnect if we were previously connected
    if (!this.hasBeenConnected) {
      this.handleInitialConnectionError(error);
      return;
    }

    eventBus.emit('connection:error', {
      error,
      recoverable: this.reconnectAttempt < RECONNECT_STRATEGY.maxAttempts,
    });

    this.scheduleReconnect(false);
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(softDisconnect: boolean): void {
    // B5-step4：清理关键帧周期重发定时器（重连期间对旧 PC 的重发无意义）
    if (this.keyframeRetryTimer !== null) {
      clearInterval(this.keyframeRetryTimer);
      this.keyframeRetryTimer = null;
    }

    // In fast reconnect mode, use fast reconnect instead
    if (this.fastReconnectMode) {
      this._log('scheduleReconnect: delegating to fast reconnect');
      this.scheduleFastReconnect();
      return;
    }

    // Don't reconnect if manually disconnected
    if (this._state === ConnectionState.DISCONNECTED) {
      this._log('Already disconnected, skip reconnect');
      return;
    }

    // Don't schedule if already scheduled
    if (this.reconnectTimer) {
      this._log('Reconnect already scheduled');
      return;
    }

    this.reconnectAttempt++;
    this._log('Scheduling reconnect attempt', this.reconnectAttempt, '/', RECONNECT_STRATEGY.maxAttempts);

    if (this.reconnectAttempt > RECONNECT_STRATEGY.maxAttempts) {
      this._log('Max reconnect attempts reached');
      this.setState(ConnectionState.FAILED);
      eventBus.emit('ui:toast', { message: '重连失败，请手动重新连接', type: 'error' });
      return;
    }

    this.setState(ConnectionState.RECONNECTING);

    eventBus.emit('connection:reconnecting', {
      attempt: this.reconnectAttempt,
      maxAttempts: RECONNECT_STRATEGY.maxAttempts,
    });

    // Calculate delay with exponential backoff and jitter
    let delay = RECONNECT_STRATEGY.baseDelay *
      Math.pow(RECONNECT_STRATEGY.backoffMultiplier, this.reconnectAttempt - 1);
    delay = Math.min(delay, RECONNECT_STRATEGY.maxDelay);
    delay = delay * (1 + (Math.random() - 0.5) * RECONNECT_STRATEGY.jitter * 2);

    // Shorter delay for soft disconnect (may recover quickly)
    if (softDisconnect) {
      delay = Math.min(delay, 2000);
    }

    this._log('Reconnect in', Math.round(delay), 'ms');

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;

      // Check if we have relay config or direct wsUrl
      if (this._relayConfig) {
        // Relay mode reconnect
        const initialPath = this.getInitialRelayPath(true);
        this.setDesiredRelayPath(initialPath, `scheduleReconnect:${softDisconnect ? 'soft' : 'hard'}`);
        this._log('Executing relay reconnect to', this._relayConfig.serverUrl, 'initialPath=', initialPath);
        this.cleanup();
        signalingService.disconnect();
        this._state = ConnectionState.DISCONNECTED;
        this.connectRelay(this._relayConfig);
      } else if (this.wsUrl) {
        // Direct mode reconnect
        const initialPath = this.getInitialRelayPath(true);
        this.setDesiredRelayPath(initialPath, `scheduleReconnect:${softDisconnect ? 'soft' : 'hard'}`);
        this._log('Executing reconnect to', this.wsUrl, 'initialPath=', initialPath);
        this.cleanup();
        signalingService.disconnect();
        this._state = ConnectionState.DISCONNECTED;
        this.connect(this.wsUrl);
      } else {
        this._error('No wsUrl or relayConfig saved for reconnect');
        this.setState(ConnectionState.FAILED);
        eventBus.emit('ui:toast', { message: '无法重连：缺少服务器地址', type: 'error' });
      }
    }, delay);
  }

  /**
   * Stop reconnection timer
   */
  private stopReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Set connection state
   */
  private setState(state: ConnectionState): void {
    if (this._state !== state) {
      const prev = this._state;
      this._state = state;
      this._log('setState:', state, 'pc exists:', !!this.pc);
      eventBus.emit('connection:stateChange', { state, previousState: prev, pc: this.pc });

      // 连接完成后主动触发一次内网 TURN 发现（每次成功建连/重连后刷新 endpoint + RTT）
      if (state === ConnectionState.CONNECTED && prev !== ConnectionState.CONNECTED) {
        this._log('Connection established, auto-triggering internal TURN rediscovery');
        this.rediscoverInternalTurn();
        this.forwardConnectFailures = 0;  // 连上即清零 forced forward 失败计数

        // PathObserver 启动：初连默认 direct；forward 接管后的整连重连必须延续 desiredRelayPath。
        // p2p 切换不经过 setState（保持 CONNECTED），不会误重置。
        const initialPath: PathId = this.config.hostInternalTurnOnly ? 'forward' : this._desiredRelayPath;
        this.setCurrentPath(initialPath, this.config.hostInternalTurnOnly ? 'internal_only' : 'preserved_reconnect');
        pathObserver.start(initialPath);
      }
    }
  }

  // ==================== ICE Racing Event Handlers ====================

  /**
   * Handle offer from Host for ICE Racing
   */
  private handleRaceOffer(data: { raceId: string; offer: RTCSessionDescriptionInit }): void {
    // PathMigration 备用连接复用 webrtcviewer+raceId 通道：pm- 前缀绝不能进 IceRacingService
    if (data.raceId.startsWith('pm-')) {
      pathMigrationService.handleOffer(data.raceId, data.offer);
      return;
    }

    if (!this.useIceRacing) return;

    this._log('ICE Racing: received offer for', data.raceId);

    // Start racing if not already started
    if (!iceRacingService.racing) {
      this._log('ICE Racing: starting race');
      // Racing is started by the first offer, not by startRace()
      // The Host sends offers for each raceId
    }

    iceRacingService.handleOffer(data.raceId, data.offer);
  }

  /**
   * Handle ICE candidate from Host for ICE Racing
   */
  private handleRaceCandidate(data: { raceId: string; candidate: RTCIceCandidateInit }): void {
    // pm- 前缀分流给 PathMigrationService（同 offer）
    if (data.raceId.startsWith('pm-')) {
      pathMigrationService.handleCandidate(data.raceId, data.candidate);
      return;
    }

    if (!this.useIceRacing) return;

    iceRacingService.handleCandidate(data.raceId, data.candidate);
  }

  /**
   * Handle ICE Racing winner
   */
  private handleRaceWinner(data: RaceWinnerEvent): void {
    this._log('ICE Racing winner:', data.raceId, '(' + data.connectionType + ') in', data.connectTimeMs, 'ms');
    this._log('Winner data - PC:', !!data.pc, 'DC:', !!data.dataChannel, 'Stream:', !!data.remoteStream, 'TURN:', data.turnProtocol || 'N/A');

    // Set the known TURN protocol for stats display
    if (data.connectionType === 'relay' && data.turnProtocol) {
      statsService.setKnownTurnProtocol(data.turnProtocol);
      // TURN TCP 模式通知 NetworkAdaptive 降级
      if (data.turnProtocol === 'TCP') {
        setTimeout(() => {
          this._log('TURN TCP 模式，通知 NetworkAdaptive 降级');
          networkAdaptiveService.forceConfig(NetworkQuality.BAD);
        }, 500);
      }
    } else {
      statsService.setKnownTurnProtocol(null);
    }
    // relayServer 现在由 StatsService 从 WebRTC stats 的 local-candidate url 字段动态获取
    // 不再从 iceServers 配置中提取（之前的方式总是取第一个 TURN URL，不准确）

    // [TCP 修复 C] 必须在 setupDataChannelEvents 之前设置 pendingWinnerToast
    // 否则 UDP 路径下 setupDataChannelEvents 会立刻同步触发 onDataChannelReady 而拿不到 toast 信息
    const connTypeText = data.connectionType === 'relay' ? '中转' : 'P2P';
    this.pendingWinnerToast = { text: connTypeText, ms: data.connectTimeMs };
    // 先切到 CONNECTING：UDP 路径下 setupDataChannelEvents 会立刻覆盖到 CONNECTED；
    //                    TCP 路径下保持 CONNECTING 直到 DC 真就绪
    this.setState(ConnectionState.CONNECTING);

    // Use the winning connection
    this.pc = data.pc;
    this.dataChannel = data.dataChannel;
    this.mouseChannel = data.mouseChannel;
    // fs-rpc / file-ctrl / file-data 改为懒加载，不再从 racing winner 接管
    this.fsRpcChannel = null;
    this._remoteStream = data.remoteStream;

    // Set up events for the winning PC
    this.setupPeerConnectionEventsForRaceWinner(data.pc);
    this.setupDataChannelEvents();   // UDP 路径：DC 已 open → 立刻 onDataChannelReady → setState(CONNECTED) + 弹 toast
    this.setupMouseChannelEvents();

    // Attach video stream
    this._log('Attaching video - element:', !!this.videoElement, 'stream:', !!this._remoteStream);
    if (this.videoElement && this._remoteStream) {
      this._log('Video tracks:', this._remoteStream.getVideoTracks().length);
      this.videoElement.srcObject = this._remoteStream;

      // Set ultra-low latency on the winning PC's video receiver
      const videoReceiver = data.pc.getReceivers().find(r => r.track?.kind === 'video');
      if (videoReceiver) {
        if ('jitterBufferTarget' in videoReceiver) {
          (videoReceiver as unknown as { jitterBufferTarget: number | null }).jitterBufferTarget = 0;
          this._log('Race winner: Set jitterBufferTarget = 0 (minimize)');
        }
        if ('playoutDelayHint' in videoReceiver) {
          (videoReceiver as unknown as { playoutDelayHint: number }).playoutDelayHint = 0;
          this._log('Race winner: Set playoutDelayHint = 0');
        }
      }

      this.videoElement.play().catch((e) => {
        this._log('Video play failed:', e.message);
      });
    } else {
      this._warn('Cannot attach video - missing element or stream');
    }

    // Handle audio tracks from the winner's stream
    if (this._remoteStream) {
      const audioTracks = this._remoteStream.getAudioTracks();
      this._log('Audio tracks from winner:', audioTracks.length);
      audioTracks.forEach((track, i) => {
        // Create a separate audio stream for each track
        const audioStream = new MediaStream([track]);
        // Determine audio source by track ID
        if (track.id.includes('system')) {
          eventBus.emit('audio:systemTrack', audioStream);
          this._log('System audio track ready (from racing winner)');
        } else if (track.id.includes('mic')) {
          eventBus.emit('audio:micTrack', audioStream);
          this._log('Microphone track ready (from racing winner)');
        } else {
          // Unknown audio track, treat as system audio
          eventBus.emit('audio:systemTrack', audioStream);
          this._log('Unknown audio track, treating as system audio (from racing winner)');
        }
      });
    }

    // [TCP 修复 C] 8 秒兜底：DC 在 winner 后还不开就当连接失败触发重连
    // pendingWinnerToast / setState(CONNECTING) 已在函数开头处理
    // UDP 路径下 setupDataChannelEvents 会立刻清掉 pendingWinnerToast，UDP 不会触发这个 timer
    if (this.dcReadyTimeoutTimer) {
      clearTimeout(this.dcReadyTimeoutTimer);
    }
    this.dcReadyTimeoutTimer = window.setTimeout(() => {
      this.dcReadyTimeoutTimer = null;
      const dcOpen = this.dataChannel?.readyState === 'open';
      if (!dcOpen && this.pendingWinnerToast !== null) {
        this._error('DataChannel did not become ready within 8s after race winner, reconnecting');
        eventBus.emit('ui:toast', { message: '数据通道开启超时，正在重连', type: 'warning' });
        this.handleRaceFailed({ reason: 'dc_ready_timeout' });
      }
    }, 8000);

  }

  /**
   * Handle ICE Racing failed
   */
  private handleRaceFailed(data: { reason: string }): void {
    this._error('ICE Racing failed:', data.reason);
    // forced forward 逃生通道：连续 2 次失败回退 direct（internal-only 测试开关豁免——
    // 其语义就是只许 forward，0-TURN 时已有 race_status error 链路提示关开关）
    if (this._desiredRelayPath === 'forward' && !this.config.hostInternalTurnOnly) {
      this.forwardConnectFailures++;
      this._log(`forward race failure ${this.forwardConnectFailures}/2 (desiredRelayPath=forward, reason=${data.reason})`);
      if (this.forwardConnectFailures >= 2) {
        this.forwardConnectFailures = 0;
        this.setDesiredRelayPath('direct', 'forward_unavailable_fallback');
        this._log('desiredRelayPath fallback: forward failed 2x consecutive → direct');
        eventBus.emit('ui:toast', { message: '内网转发线路连接失败，已自动回退直连线路', type: 'warning' });
      }
    }
    // [TCP 修复 C] 清理 winner 待确认状态，防止 8s 兜底 timer 残留
    if (this.dcReadyTimeoutTimer) {
      clearTimeout(this.dcReadyTimeoutTimer);
      this.dcReadyTimeoutTimer = null;
    }
    this.pendingWinnerToast = null;
    this.setState(ConnectionState.FAILED);
    eventBus.emit('ui:toast', { message: 'ICE Racing 失败，所有连接尝试均失败', type: 'error' });
  }

  /**
   * Handle peer status change (for ICE Racing)
   * When Host comes online, start ICE Racing
   */
  private handlePeerStatus(data: { status: string; peerType: string }): void {
    if (!this._relayConfig) return;  // Only for relay mode

    if (data.peerType === 'Host' && data.status === 'offline') {
      // Host went offline - immediately clean up old connection for fast reconnect
      this._log('Host offline (peer_status), cleaning up old connection');
      this.prepareForFastReconnect();
      return;
    }

    if (!this.useIceRacing) return;

    if (data.peerType === 'Host' && data.status === 'online') {
      // 检查 ICE 策略
      const forceRelay = this.config.icePolicy === 'relay';
      const disableTurn = this.config.icePolicy === 'p2p';
      const modeStr = forceRelay ? ' (forceRelay)' : disableTurn ? ' (P2P only)' : '';
      console.log(`[WebRTC] Host is online, starting ICE Racing${modeStr}`);
      const desiredPath = this.getInitialRelayPath(true);
      this.setDesiredRelayPath(desiredPath, 'startRace');
      iceRacingService.startRace(
        forceRelay,
        disableTurn,
        this.config.turnProtocol || 'auto',
        this.config.videoCodec,
        !!this.config.hostInternalTurnOnly,
        desiredPath,
      );
    }
  }

  /**
   * Handle host:waiting event from SignalingService's scheduleReconnect
   * Clean up old PC on first attempt so ICE Racing can start fresh
   */
  private handleHostWaiting(data: { attempt: number; maxAttempts: number }): void {
    if (data.attempt === 1) {
      this._log('Host offline (host:waiting), cleaning up old connection for fast reconnect');
      this.prepareForFastReconnect();
    }
  }

  /**
   * Prepare for fast reconnect by cleaning up old connection immediately
   * Called when host goes offline - don't wait for slow ICE timeout
   */
  private prepareForFastReconnect(): void {
    // Only act if we were previously connected
    if (!this.hasBeenConnected) return;

    // Already cleaned up
    if (this._state === ConnectionState.RECONNECTING || this._state === ConnectionState.DISCONNECTED) return;

    this._log('Preparing for fast reconnect: cleaning up old PC (was:', this._state, ')');

    // Stop any pending reconnect timers (SignalingService handles reconnection)
    this.stopReconnect();

    // [TCP 修复 C] 清理 winner 待确认状态
    if (this.dcReadyTimeoutTimer) {
      clearTimeout(this.dcReadyTimeoutTimer);
      this.dcReadyTimeoutTimer = null;
    }
    this.pendingWinnerToast = null;

    // Close old PeerConnection immediately (don't wait for ICE timeout)
    if (this.pc) {
      this.pc.oniceconnectionstatechange = null;
      this.pc.onconnectionstatechange = null;
      this.pc.ontrack = null;
      this.pc.ondatachannel = null;
      this.pc.close();
      this.pc = null;
    }
    this.dataChannel = null;
    this.mouseChannel = null;
    this.fsRpcChannel = null;

    // Reset state so ICE Racing can start fresh when host comes back
    this.reconnectAttempt = 0;
    this.setState(ConnectionState.RECONNECTING);
  }

  // Timer for race winner disconnect handling
  private raceWinnerDisconnectTimer: number | null = null;

  /**
   * Setup peer connection events for race winner (minimal - PC is already connected)
   */
  private setupPeerConnectionEventsForRaceWinner(pc: RTCPeerConnection): void {
    // Monitor ICE connection state for disconnects
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      this._log('Race winner ICE state:', state);
      eventBus.emit('webrtc:iceState', state as RTCIceConnectionState);

      // Clear any pending disconnect timer when state changes
      if (this.raceWinnerDisconnectTimer) {
        clearTimeout(this.raceWinnerDisconnectTimer);
        this.raceWinnerDisconnectTimer = null;
      }

      if (state === 'disconnected') {
        // ICE disconnected can be temporary (e.g., during renegotiation)
        // Wait 5 seconds before triggering reconnect
        this._log('Race winner ICE disconnected, waiting 5s for recovery...');
        this.raceWinnerDisconnectTimer = window.setTimeout(() => {
          if (pc.iceConnectionState === 'disconnected' && this.hasBeenConnected) {
            if (!this.intentionalDisconnect) {
              logConnectionError('ICE连接断开超过5秒未恢复', {
                connectionState: pc.connectionState,
              });
            }
            this.scheduleReconnect(true);
          }
        }, 5000);
      } else if (state === 'failed') {
        if (!this.intentionalDisconnect) {
          logConnectionError('ICE连接失败', {
            connectionState: pc.connectionState,
          });
        }
        if (this.hasBeenConnected) {
          this.scheduleReconnect(false);
        }
      } else if (state === 'closed') {
        this.setState(ConnectionState.DISCONNECTED);
      } else if (state === 'connected' || state === 'completed') {
        // Connection recovered
        this._log('Race winner ICE recovered:', state);
        this.stopReconnect();
        if (this._state === ConnectionState.RECONNECTING) {
          this.setState(ConnectionState.CONNECTED);
        }
      }
    };

    // Handle new tracks
    pc.ontrack = (event) => {
      this._log('Race winner received track:', event.track.kind);
      if (event.track.kind === 'video') {
        this._remoteStream = event.streams[0] || new MediaStream([event.track]);
        if (this.videoElement) {
          this.videoElement.srcObject = this._remoteStream;

          // Set ultra-low latency for P2P winner
          if (event.receiver) {
            if ('jitterBufferTarget' in event.receiver) {
              (event.receiver as unknown as { jitterBufferTarget: number | null }).jitterBufferTarget = 0;
              this._log('Race winner: Set jitterBufferTarget = 0');
            }
            if ('playoutDelayHint' in event.receiver) {
              (event.receiver as unknown as { playoutDelayHint: number }).playoutDelayHint = 0;
            }
          }

          this.videoElement.play().catch(() => {});
          // B5-step2：winner 视频 track 到达后周期请求关键帧直到首个 IDR 解码（成功即停）
          this.startKeyframeRetryUntilFrame('race-winner-ontrack');
        }
      }
    };

    // 接收 Host 创建的通道（file-ctrl/file-data/data 通道上的消息处理）
    this.setupOnDataChannelHandler(pc);
  }

  /**
   * 设置 PC 的 ondatachannel 处理器（接收 Host 创建的通道）
   * 用于 ICE Racing winner 和 P2P Assist 切换后的新 PC
   */
  private setupOnDataChannelHandler(pc: RTCPeerConnection): void {
    pc.ondatachannel = (event) => {
      const channel = event.channel;
      this._log('Received Host DataChannel:', channel.label);

      // Host 创建的 file-ctrl/file-data 通道 → 转发给 FileTransferService
      if (channel.label === 'file-ctrl' || channel.label === 'file-data') {
        this._log('Host-created file channel received:', channel.label);
        eventBus.emit('fileTransfer:hostChannel', { label: channel.label, channel });
        return;
      }

      // [TCP 修复 A] data 通道：racing 阶段 800ms 没等到的 DC 现在补挂
      // TURN-TCP 路径下 DTLS+SCTP+DCEP 通常 1-3s 才完成，会落到这条路径
      if (channel.label === 'data') {
        if (!this.dataChannel) {
          this._log('Late-arrival data channel after race winner, attaching');
          this.dataChannel = channel;
          this.setupDataChannelEvents();  // 复用现有事件初始化（含 onopen → onDataChannelReady）
        } else {
          this._log('data channel already attached, ignoring duplicate from ondatachannel');
        }
        return;
      }

      // [TCP 修复 A] mouse 通道同理
      if (channel.label === 'mouse') {
        if (!this.mouseChannel) {
          this._log('Late-arrival mouse channel after race winner, attaching');
          this.mouseChannel = channel;
          this.setupMouseChannelEvents();
        } else {
          this._log('mouse channel already attached, ignoring duplicate from ondatachannel');
        }
        return;
      }

      // [TCP 修复 A] quality-test 通道在 racing 之后到达就没用了，直接关掉避免内存泄漏
      if (channel.label === 'quality-test') {
        this._log('quality-test channel arrived after race, closing (no longer needed)');
        try { channel.close(); } catch { /* noop */ }
        return;
      }

      // 历史兜底：未知 label 的通道，挂个 onmessage 处理 cursor/stats/clipboard
      // 实际上 data/mouse/quality-test/file-* 已经全部覆盖，走到这里说明 host 加了新通道
      this._warn('Unexpected channel label after race winner:', channel.label);
      channel.onmessage = (e) => {
        try {
          let text: string;
          const d = e.data;
          if (typeof d === 'string') {
            text = d;
          } else if (d?.byteLength !== undefined) {
            text = new TextDecoder().decode(d);
          } else {
            return;
          }
          const data = JSON.parse(text);
          if (data.type === 'cursor') {
            eventBus.emit('input:cursor', data);
          } else if (data.type === 'server_stats') {
            eventBus.emit('stats:rawServerStats', data.stats);
          } else if (data.type === 'clipboard') {
            eventBus.emit('clipboard:sync', data.text || '');
          } else if (data.type === 'clipboard_query_result') {
            clipboardQueryService.handleResult(data);
          } else if (data.type === 'agent_restart') {
            eventBus.emit('agent:restart');
          }
        } catch {
          // Not JSON, ignore
        }
      };
    };
  }

  // ==================== End ICE Racing Event Handlers ====================

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    this.stopVisibilityMonitor();
    this.stopIdleMonitor();

    // 路径观察/迁移随连接生命周期复位（两者均幂等，重复调用静默）；账本/冷却跨连接保留
    pathObserver.reset('disconnect');
    pathMigrationService.abort('reset');

    // 取消所有 pending clipboard_query，避免 promise 永挂
    clipboardQueryService.cancelAll();

    // Clear race winner disconnect timer
    if (this.raceWinnerDisconnectTimer) {
      clearTimeout(this.raceWinnerDisconnectTimer);
      this.raceWinnerDisconnectTimer = null;
    }

    if (this.decodeStallRecoveryTimer !== null) {
      clearTimeout(this.decodeStallRecoveryTimer);
      this.decodeStallRecoveryTimer = null;
    }

    // [TCP 修复 C] 清理 winner 待确认状态
    if (this.dcReadyTimeoutTimer) {
      clearTimeout(this.dcReadyTimeoutTimer);
      this.dcReadyTimeoutTimer = null;
    }
    this.pendingWinnerToast = null;

    // B5-step4：清理关键帧周期重发定时器
    if (this.keyframeRetryTimer !== null) {
      clearInterval(this.keyframeRetryTimer);
      this.keyframeRetryTimer = null;
    }

    // Cancel any ongoing ICE Racing
    if (iceRacingService.racing) {
      iceRacingService.cancel();
    }

    if (this.dataChannel) {
      this.dataChannel.onopen = null;
      this.dataChannel.onclose = null;
      this.dataChannel.onerror = null;
      this.dataChannel.onmessage = null;
      this.dataChannel.close();
      this.dataChannel = null;
    }

    if (this.mouseChannel) {
      this.mouseChannel.onopen = null;
      this.mouseChannel.onclose = null;
      this.mouseChannel.onerror = null;
      this.mouseChannel.close();
      this.mouseChannel = null;
    }

    if (this.fsRpcChannel) {
      this.fsRpcChannel.onopen = null;
      this.fsRpcChannel.onclose = null;
      this.fsRpcChannel.onerror = null;
      this.fsRpcChannel.close();
      this.fsRpcChannel = null;
    }

    if (this.pc) {
      this.pc.onicecandidate = null;
      this.pc.oniceconnectionstatechange = null;
      this.pc.ontrack = null;
      this.pc.ondatachannel = null;
      this.pc.close();
      this.pc = null;
    }

    this._remoteStream = null;
    this.localCandidates = [];
    this.pendingRemoteCandidates = [];
  }
}

// Singleton instance
export const webRTCService = new WebRTCService();
export default webRTCService;
